// Articles migrated from the old site serve at their original root URLs (exact same
// paths as the live site); newer guides live under /blog/.
export const LEGACY_SLUGS = new Set([
  "5-signs-its-time-to-update-your-websites-content-and-how-to-do-it-right",
  "double-your-leads-in-just-3-months",
  "5-reasons-to-update-your-wordpress-website",
  "fresh-content",
  "much-seo-need-small-business",
  "3-benefits-a-properly-seo-optimized-website-can-give-you",
  "10-ways-to-screw-up-your-online-ad-campaign",
  "design-lead-pages-better-conversions",
  "website-looking-dated-5-key-elements-for-a-redesign",
  "top-10-ways-to-improve-your-website-user-experience",
  "attract-more-local-customers",
  "how-to-create-an-effective-google-business-profile-for-local-seo",
  "google-seller-ratings-for-ecommerce-sites",
  "7-email-capture-opt-tips-improve-conversions",
  "is-your-website-mobile-ready-why-your-mobile-site-needs-to-be-optimized-for-conversion",
  "how-to-use-facebook-custom-audiences-in-power-editor-for-ad-targeting",
]);

export const articleHref = (a) =>
  LEGACY_SLUGS.has(a.slug) ? `/${a.slug}` : `/blog/${a.slug}`;

export const ARTICLES = [
  {
    slug: "kansas-city-geo-for-home-service-businesses",
    links: [
      { label: "AI Visibility Program", to: "/ai-visibility", note: "The program that ran these campaigns" },
      { label: "Kansas City Home Services & Local Search", to: "/blog/kansas-city-home-services-local-search", note: "The local foundation AI engines read" },
      { label: "How to Show Up in AI Answers", to: "/blog/how-to-show-up-in-ai-answers", note: "The seven-step owner's checklist" },
    ],
    category: "AI Search",
    graphic: "/img/blog/kc-geo-home-services.svg",
    title: "Kansas City GEO for Home Service Businesses: 3 Case Studies",
    description:
      "How Kansas City plumbers, HVAC and roofing companies get named in ChatGPT and Gemini answers — three case studies where AI visibility landed in 2-3 months.",
    excerpt:
      "KC homeowners ask ChatGPT \"who's a good plumber?\" instead of searching. Three home service case studies — most saw AI visibility inside 2-3 months.",
    readTime: "6 min read",
    date: "2026-11-02",
    content: [
      {
        type: "p",
        text: "A homeowner in Brookside wakes up to water in the basement and asks ChatGPT \"who's a good plumber in Kansas City?\" — not Google. The answer names two or three businesses. We covered [what GEO is and how it works](/blog/what-is-geo-how-does-it-work); this one is what it looks like for home service companies in the KC metro, with three recent programs and how long each took to show up.",
      },
      { type: "h2", text: "Why home services gets pulled into AI answers first" },
      {
        type: "p",
        text: "Urgent, local, high-intent questions are exactly what people hand to an AI engine: \"who can replace a water heater in Olathe today\", \"best HVAC company in Overland Park for a furnace that won't start\". The engine blends what it has already read about local businesses — [your website](/blog/kansas-city-home-services-local-search), your [Google Business Profile](/google-business-profile), directories, review sites — with what it can look up on the spot, then answers with a short list. If your facts are thin or inconsistent, the engine hedges toward whoever's are clean. That's the whole game: become the business the engine can verify.",
      },
      { type: "h2", text: "The work, in 60 seconds" },
      {
        type: "ul",
        items: [
          "**Fix the facts** — identical name, services, service areas and hours on your website, Google Business Profile and every listing.",
          "**Make pages quotable** — plain sentences that say what you do, where you do it, and what it costs, with question-shaped headings answered directly.",
          "**Open the doors** — let AI crawlers read your site the same way Googlebot does, and add an llms.txt cheat sheet.",
          "**Stack the proof** — recent reviews, named testimonials, photos of real jobs, mentions from other local sites.",
          "**Track it** — ask each engine the question your customer would, monthly, and log named / quoted / absent.",
        ],
      },
      { type: "h2", text: "Three case studies — 2 to 3 months in" },
      {
        type: "p",
        text: "Three programs from our own roster, trimmed to what matters. We track an agreed list of target questions per engine in the campaign dashboard, so \"showed up\" means named or quoted in an AI answer for those tracked questions — not vibes.",
      },
      { type: "h2", text: "Case study 1 — Plumbing company, Lee's Summit (first call by week 9)" },
      {
        type: "p",
        text: "A two-truck plumbing shop. Solid reviews, invisible in every AI engine. Month one was the fundamentals: service pages rewritten to answer the questions homeowners actually type (\"how much does water heater replacement cost in Lee's Summit?\"), facts made identical everywhere, crawlers opened. **Week 9: a call came in — \"ChatGPT sent me.\"** By week 12 the shop was named for 4 of their 7 tracked questions across at least one engine, up from zero.",
      },
      { type: "h2", text: "Case study 2 — HVAC contractor, Overland Park (quoted by week 6)" },
      {
        type: "p",
        text: "Residential heating and cooling. The fix that moved them wasn't a trick — it was cost pages: honest ranges for furnace replacement and AC installs, written so a machine could quote them. **By week 6, Gemini was quoting their price ranges back in answers**; the first AI-attributed booking landed in week 7. By month three they were named or quoted in 9 of 12 tracked target questions.",
      },
      { type: "h2", text: "Case study 3 — Roofing company, Kansas City's Northland (named by week 8)" },
      {
        type: "p",
        text: "Storm season makes roofing brutal — and AI is where homeowners now start after hail. We built storm-answer pages (what a post-hail inspection covers, what insurance adjusters look for), refreshed the profile with storm-season updates, and cleaned up citations. **By week 8 the roofer was being named alongside the metro's biggest brands**; by week 11 they appeared for 5 of 9 tracked questions, with two bookings that month citing AI.",
      },
      { type: "img", src: "/img/blog/kc-geo-home-services.svg", alt: "Illustration of a Kansas City house beside an AI answer card naming a local home service business" },
      { type: "h2", text: "What made the difference" },
      {
        type: "ul",
        items: [
          "**Plain sentences win.** Every case turned on pages that state the trade, the towns and the price range in words an engine can quote.",
          "**Identical facts everywhere.** One phone number, one service list, one service area — matched on site, profile and listings.",
          "**Proof with recency.** Reviews from the last 90 days outweigh a wall of old ones.",
          "**Question-shaped pages.** Headings written the way the customer asks them, answered in the first two sentences.",
          "**Patience with the cadence.** Engines refresh on their own schedule — month one is setup, months two and three are when the answers start naming you.",
        ],
      },
      { type: "h2", text: "Where the 2-3 months fits" },
      {
        type: "p",
        text: "The pattern across programs: month one is plumbing work nobody sees — facts, crawlers, pages. Month two the engines start trusting the sources. Month three it compounds, and the tracked questions start coming back with your name in them. That's why [our AI Visibility Program](/ai-visibility) runs on a six-month campaign with a written guarantee on your tracked target searches — and why we report named / quoted / absent every month, so you're never guessing. It pairs naturally with a [full SEO campaign](/seo) when you want the Google side covered too.",
      },
      { type: "h2", text: "Try it this week" },
      {
        type: "ul",
        items: [
          "Ask ChatGPT and Gemini: \"Who's a good [your trade] in [your suburb]?\" — and write down who gets named.",
          "Check your robots.txt allows the AI crawlers, and read your homepage out loud — if it doesn't plainly say what you do and where, fix that first.",
          "Or [book a free call with Todd](/free-strategy-call) and we'll run the audit with you.",
        ],
      },
      {
        type: "bottom",
        text: "Bottom line: Kansas City GEO for home service businesses means being the named answer when a homeowner asks AI who to call — and in our programs it typically starts landing within 2 to 3 months. The [seven steps to show up in AI answers](/blog/how-to-show-up-in-ai-answers) are the playbook; the AI Visibility Program is the routine that runs them.",
      },
    ],
  },
  {
    slug: "how-to-show-up-in-ai-answers",
    links: [
      { label: "SEO + AI Visibility Program", to: "/ai-visibility", note: "The program behind being the answer" },
      { label: "What Is GEO, How Does It Work?", to: "/blog/what-is-geo-how-does-it-work", note: "The explainer behind these steps" },
      { label: "The AI Answer Checklist", to: "/ai-answer-checklist", note: "The shareable 7-point version" },
    ],
    category: "AI Search",
    graphic: "/img/blog/ai-answers-steps.svg",
    title: "How to Show Up in AI Answers: Steps for Owners",
    description:
      "Seven practical steps to get named in ChatGPT, Gemini and Claude answers: quotable facts, question-shaped pages, real proof, open crawlers, monthly audits.",
    excerpt:
      "You can't buy the mention — you earn it. Seven steps any owner can take to become the business AI engines name, quote and recommend.",
    readTime: "7 min read",
    date: "2026-10-22",
    content: [
      {
        type: "p",
        text: "Last time we covered [what GEO is](/blog/what-is-geo-how-does-it-work) and whether you need it. This one is the doing part: seven steps, in order, that make your business the one AI engines name when a customer asks. None of them require an agency — they require attention.",
      },
      { type: "h2", text: "Step 1 — Make your facts identical everywhere" },
      {
        type: "p",
        text: "AI engines cross-check before they recommend. Your business name, services, service areas, hours and phone number should read the same on your website, your Google Business Profile and every listing — one disagreement and the engines hedge toward a source they can verify. Pick the canonical version of each fact and propagate it.",
      },
      { type: "h2", text: "Step 2 — Say what you do in plain sentences" },
      {
        type: "p",
        text: "**Engines quote sentences, not slogans.** A page that states \"We repair residential furnaces and heat pumps in Kansas City's northland suburbs\" is quotable. \"Comfort redefined\" is not. Walk your homepage and service pages and make sure each one says what you do, for whom, and where — in the first paragraph, in plain words.",
      },
      { type: "h2", text: "Step 3 — Shape pages as questions with answers" },
      {
        type: "ul",
        items: [
          "Headings your customers actually ask: \"How much does furnace replacement cost?\" — then answer directly underneath.",
          "Short paragraphs, lists where they help, one idea per section.",
          "The answer in the first two sentences of each section — engines and humans both reward the direct version.",
        ],
      },
      { type: "h2", text: "Step 4 — Stack the proof" },
      {
        type: "p",
        text: "Recommendations run on evidence: a complete [Google Business Profile](/google-business-profile) with a steady stream of honest reviews, testimonials with names attached, photos of real jobs, and mentions from other sites in your world — partners, suppliers, local directories. The proof isn't decoration; it's the citation trail an AI follows before naming you.",
      },
      { type: "img", src: "/img/blog/ai-answers-steps.svg", alt: "Illustration of a step path from business facts to an AI answer with a checkmark" },
      { type: "h2", text: "Step 5 — Let the machines read" },
      {
        type: "p",
        text: "If your robots.txt blocks AI crawlers, you're invisible by choice. Allow the major AI crawlers (GPTBot, Claude, Gemini, Perplexity) the same way you allow Googlebot, and keep an llms.txt file that describes your business in plain text — it's the cheat sheet engines are learning to read. Ours is at /llms.txt if you want to see the pattern.",
      },
      { type: "h2", text: "Step 6 — Audit yourself monthly" },
      {
        type: "p",
        text: "Ask each major engine the question a customer would: \"Who's a good [your trade] in [your town]?\" — and read what comes back. Named? Quoted correctly? A competitor with better-organized facts in the answer instead of you? Note the gap, fix the source — usually your profile or a page that doesn't say it plainly — and ask again next month.",
      },
      { type: "h2", text: "Step 7 — Keep it fresh" },
      {
        type: "p",
        text: "Everything above decays without maintenance: new reviews, updated pages, seasonal answers, facts that stay matched. **AI visibility isn't a switch — it's a routine**, the same way your truck maintenance is.",
      },
      {
        type: "bottom",
        text: "Bottom line: showing up in AI answers is earned with quotable facts, question-shaped pages, visible proof and a monthly audit — seven steps any owner can start this week. If you'd rather have the routine run for you, that's literally our [SEO + AI Visibility program](/ai-search-visibility).",
      },
    ],
  },
  {
    slug: "what-is-geo-how-does-it-work",
    links: [
      { label: "SEO & Google Visibility", to: "/seo", note: "The foundation everything AI reads" },
      { label: "Google Business Profile Guide for Home Services", to: "/how-to-create-an-effective-google-business-profile-for-local-seo", note: "The profile AI checks for local proof" },
    ],
    category: "AI Search",
    graphic: "/img/blog/what-is-geo.svg",
    title: "What Is GEO, How Does It Work? Do I Need It?",
    description:
      "GEO — Generative Engine Optimization — is how businesses get named in AI answers: ChatGPT, Gemini, Claude and AI Overviews. What it is, and do you need it?",
    excerpt:
      "Customers increasingly ask AI instead of Google. Here's what GEO actually means, how AI engines pick who to recommend, and an honest answer on whether you need it.",
    readTime: "6 min read",
    date: "2026-10-22",
    content: [
      {
        type: "p",
        text: "Type \"who's a good plumber near me\" into ChatGPT, Gemini or Google's AI Overviews and you'll get an answer with a handful of businesses named — some linked, some backed by reviews, none of them paid slots. GEO — Generative Engine Optimization — is the work of making sure your business is one of the names. This page explains what it is, how the engines pick, and gives you the honest answer on whether you personally have to have it.",
      },
      { type: "h2", text: "What GEO is (in one paragraph)" },
      {
        type: "p",
        text: "**Search engines rank pages; AI engines answer questions.** GEO is the practice of making your business the kind of source an AI engine can find, trust and quote when a customer asks for a recommendation. Where [SEO](/seo) aims to rank a page, GEO aims to get your business named inside the answer itself — with your reviews, your service list and your website attached as proof.",
      },
      { type: "h2", text: "How it actually works" },
      {
        type: "p",
        text: "When an AI engine answers \"who should I call for AC repair in Olathe\", it blends things it has read about the world with things it can look up right now — and it shows its work with citations. What it gravitates toward is consistent, verifiable business information: a complete [Google Business Profile](/google-business-profile) with real reviews, a website that states plainly what you do and where, pages that answer questions directly instead of dancing around them, and the same facts matching everywhere. What it can't use is vague marketing fluff — an AI can't quote what it can't understand.",
      },
      { type: "img", src: "/img/blog/what-is-geo.svg", alt: "Illustration of an AI chat answer citing a business with reviews and sources" },
      { type: "h2", text: "How it differs from SEO (and why they overlap)" },
      {
        type: "ul",
        items: [
          "SEO earns the click; GEO earns the mention. One optimizes rankings, the other optimizes recommendations.",
          "The foundation is the same work: a fast, clear website; complete business information; genuine reviews; content that answers real questions.",
          "The extras differ: structured facts AI can parse, direct Q&A-style answers on your pages, consistent details across the web.",
          "Strong SEO is step one of GEO — engines read the same web your customers search.",
        ],
      },
      { type: "h2", text: "\"Do I have to have it?\" — the honest answer" },
      {
        type: "p",
        text: "Nobody's forcing you, and no — there's no law of marketing that says you must. But here's the trend worth naming: more of your customers ask an assistant instead of scrolling results every year, and the businesses getting named are the ones whose information is complete and easy to quote. If your competitors' profiles are rich and yours is thin, the answer engines have nothing to work with. So the honest version: you don't have to have GEO — you have to decide whether being invisible in an answer your customer trusts counts as an option.",
      },
      { type: "h2", text: "What to do first" },
      {
        type: "ul",
        items: [
          "Nail the fundamentals — website, Google Business Profile, reviews. They're the majority of what engines quote.",
          "Answer real customer questions in plain language on your pages.",
          "Keep your facts identical everywhere — name, services, areas, hours.",
          "Then, if you want the AI side handled deliberately, that's exactly what our [SEO + AI Visibility program](/ai-visibility) does — one offer, one person accountable.",
        ],
      },
      {
        type: "bottom",
        text: "Bottom line: GEO is making your business quotable — clear, complete and verifiable everywhere AI looks. You don't have to have it, but your customers are already asking. Build the fundamentals first, then claim the answer.",
      },
    ],
  },
  {
    slug: "google-seller-ratings-for-ecommerce-sites",
    links: [
      { label: "Website Design & Optimization", to: "/website-optimization", note: "Product pages that earn the stars" },
      { label: "How Much SEO Do I Need for My Small Business?", to: "/much-seo-need-small-business", note: "The honest answer on effort vs. outcome" },
    ],
    category: "Ecommerce",
    graphic: "/img/blog/seller-ratings-stars.svg",
    title: "Google Seller Ratings for Ecommerce: Earn the Stars",
    description:
      "Seller ratings put stars next to your ads and build instant trust with first-time buyers — what they are, how to qualify, and how to keep them. Plain English.",
    excerpt:
      "Stars next to your ads change how strangers click. Here's how Google seller ratings work for ecommerce, what it takes to qualify, and how to earn them honestly.",
    readTime: "5 min read",
    date: "2026-10-22",
    content: [
      {
        type: "p",
        text: "Two nearly identical ads sit side by side. One shows \"4.8 ★\" under the headline; the other shows nothing. For a shopper who has never heard of either store, that row of stars does the trusting in advance — and it's why **seller ratings are one of the highest-leverage, least-used assets in ecommerce**. Here's what they are and how to earn them.",
      },
      { type: "h2", text: "What seller ratings actually are" },
      {
        type: "p",
        text: "Seller ratings are the star score Google attaches to your ads, Shopping listings and search results — a rating of your STORE, collected from **Google's approved review sources**: Google Customer Reviews and recognized third-party review services. They're different from product reviews; they measure the buying experience with you — shipping, service, reliability.",
      },
      { type: "h2", text: "What it takes to qualify" },
      {
        type: "ul",
        items: [
          "Enough recent reviews from a Google-recognized source — Google's public guidance has historically pointed to 100+ unique reviews in the trailing 12 months; check current requirements when you start.",
          "A combined rating above the minimum Google displays (historically 3.5 stars).",
          "A reviewed domain serving the country where your ad shows.",
          "Reviews that are genuine — bought or incentivized reviews violate policy and risk the whole program.",
        ],
      },
      { type: "img", src: "/img/blog/seller-ratings-stars.svg", alt: "Illustration of a five-star seller rating badge beside a shopping bag and ad snippet" },
      { type: "h2", text: "Why they're worth the work" },
      {
        type: "ul",
        items: [
          "More clicks from the same spend — stars make your ad stand out, and higher click-through feeds quality scores.",
          "Trust for first-time buyers — niche-product stores especially, where shoppers can't compare three stores at the mall first.",
          "Free surface area — the ratings show not just on ads but on your [organic listing's](/seo) knowledge panel presence.",
          "A compounding moat — every month of honest reviews makes the badge harder for competitors to copy.",
        ],
      },
      { type: "h2", text: "How to start earning them" },
      {
        type: "ul",
        items: [
          "Turn on Google Customer Reviews — free: opt-in shoppers get a post-delivery survey, and Google collects the ratings.",
          "Or work with a recognized third-party review partner whose ratings Google aggregates.",
          "Ask at the right moment — after delivery, when the product has proven itself.",
          "Reply to reviewers publicly and treat recurring complaints as [product-page fixes](/website-optimization), not PR problems.",
        ],
      },
      {
        type: "bottom",
        text: "Bottom line: seller ratings turn strangers' trust into clicks before your ad is even read — and they cost nothing but a review routine run honestly. Set it up, keep the stars above the line, and let every month of good service advertise the next. If you'd like the routine built for your store, that's our whole business.",
      },
    ],
  },
  {
    slug: "geo-plus-seo-unstoppable-force",
    links: [
      { label: "AI Search Visibility — the full program", to: "/ai-search-visibility", note: "Both engines, one monthly plan" },
      { label: "What Is GEO, How Does It Work? Do I Need It?", to: "/blog/what-is-geo-how-does-it-work", note: "The primer before this one" },
      { label: "Free AI-answers checklist", to: "/ai-answer-checklist", note: "The seven steps, shareable" },
    ],
    category: "AI Search",
    graphic: "/img/blog/geo-plus-seo.svg",
    title: "GEO + SEO Is an Unstoppable Force",
    description:
      "SEO earns the ranking, GEO earns the mention. Stack Generative Engine Optimization on top of SEO and your business owns both surfaces customers use to choose.",
    excerpt:
      "Google ranks you; ChatGPT names you. Here's why stacking GEO on top of SEO compounds — with real numbers from our own case studies.",
    readTime: "6 min read",
    date: "2026-10-08",
    content: [
      {
        type: "p",
        text: "Run a Google search and an AI search side by side and you'll meet two very different winners. On Google, somebody owns page one. In ChatGPT, Gemini and Claude, somebody gets **named in the answer**. The businesses quietly winning right now aren't choosing between those outcomes — they're stacking **GEO on top of SEO** and owning both surfaces at once. That combination is an unstoppable force, and here's why.",
      },
      { type: "h2", text: "SEO gets you ranked. GEO gets you quoted." },
      {
        type: "p",
        text: "**SEO** — [search engine optimization](/seo) — earns your page a ranking when a customer searches. **GEO** — Generative Engine Optimization — makes your business the one an AI engine names inside the answer itself, with your reviews, service list and website attached as proof. Two different surfaces, the same customer, often on the same buying journey: they search first, then ask an assistant to narrow it down. Being present on only one surface means hoping the other one never matters.",
      },
      { type: "h2", text: "Why GEO rides on SEO" },
      {
        type: "p",
        text: "AI engines read the same web your customers search — and they favor sources that are easy to find and verify. That's why GEO isn't a separate discipline you bolt on later; it's the next layer up from the SEO you should be doing anyway:",
      },
      {
        type: "ul",
        items: [
          "**Same foundation.** A fast, clear website with complete business facts and genuine reviews — the majority of what engines quote comes straight from it. [Our Kansas City local search guide](/blog/kansas-city-home-services-local-search) covers the checklist.",
          "**Same content engine.** The [unique, citable content](/ai-answer-checklist) you publish for Google is exactly what AI engines learn to quote.",
          "**Same trust signals.** Backlinks, reviews and [seller ratings](/google-seller-ratings-for-ecommerce-sites) compound on both surfaces at once.",
          "**One extra layer.** Crawler access and answer-shaped pages — [how to show up in AI answers](/blog/how-to-show-up-in-ai-answers) — get you named, not just ranked.",
        ],
      },
      { type: "h2", text: "The compounding effect (real numbers)" },
      {
        type: "p",
        text: "We watch this stack work in our own case studies. A restoration client's aligned SEO campaign gained [500% more organic traffic in 6 months](/case-studies/floodmasters) — and the content that earned those rankings is what AI engines now quote. An engineering client's optimized ads and website brought [cost per click down 80% while lead volume rose 350%](/case-studies/kc-engineering) — because the site underneath the ads converted everything the ads touched. **SEO fills the funnel; GEO makes sure the machines summarizing the funnel say your name.**",
      },
      { type: "img", src: "/img/blog/geo-plus-seo.svg", alt: "SEO ranking and GEO quoting feeding one business result" },
      { type: "h2", text: "What stacking looks like week to week" },
      {
        type: "ul",
        items: [
          "**Fix the site** — speed, structure and service pages that state plainly what you do and where.",
          "**Publish** — one genuinely useful, specific piece at a time; [fresh content beats stale every time](/fresh-content).",
          "**Keep facts identical** — profile, website and listings reading the same everywhere.",
          "**Open the AI door** — crawler rules, llms.txt and Q&A-shaped pages.",
          "**Measure both** — rankings in the dashboard, mentions in the engines.",
        ],
      },
      { type: "h2", text: "How to start" },
      {
        type: "p",
        text: "If you want both engines handled under one roof, our [AI Search Visibility program](/ai-search-visibility) bundles everything above for **$597/mo**, month-to-month — and founding clients [lock $397/mo](/ai-visibility) while spots last. Prefer to start yourself? Work through the [free AI-answers checklist](/ai-answer-checklist) first and read [what GEO actually is](/blog/what-is-geo-how-does-it-work) — then talk to us.",
      },
      {
        type: "bottom",
        text: "Bottom line: **SEO wins the search results; GEO wins the answer.** Stack them and you're unmissable on both surfaces your customers use — and that combination is what compounds month after month.",
      },
    ],
  },
  {
    slug: "google-ads-for-hvac-restoration-home-services",
    links: [
      { label: "Google Ads Management", to: "/ad-remarketing", note: "Search intent, turned into calls" },
      { label: "Packages", to: "/packages", note: "See the Google Ads offer at a glance" },
    ],
    category: "Paid Ads",
    graphic: "/img/blog/ads-home-services.svg",
    title: "Google Ads for Home Services: The $1M to $4M Playbook",
    description:
      "Why Google Ads fits home services perfectly — emergency intent, high job value — and the playbook behind a client's growth from $1M to $4M at half the ad cost.",
    excerpt:
      "Home-service buyers search with urgency and high job value — the exact conditions Google Ads was built for. Here's the playbook that took a client from $1M to $4M.",
    readTime: "6 min read",
    date: "2026-10-21",
    content: [
      {
        type: "p",
        text: "A burst pipe at 11pm doesn't browse. Neither does a furnace that dies on the coldest night of the year. Home-service customers search with urgency, pick fast, and the job value is high — which is exactly why [Google Ads](/ad-remarketing), aimed properly, is the strongest paid channel an HVAC, restoration, plumbing or electrical company can run. Here's the playbook — and the receipts.",
      },
      { type: "h2", text: "Why ads fit home services better than most industries" },
      {
        type: "ul",
        items: [
          "Emergency intent: 'water heater leaking' searches convert now, not next quarter.",
          "High job value: one installation can pay for a month of ad spend many times over.",
          "Local targeting: you only bid where your trucks actually go — no wasted reach.",
          "Measurable calls: call assets and tracking show which searches produce the phone ringing.",
        ],
      },
      { type: "h2", text: "The playbook, in five moves" },
      {
        type: "ul",
        items: [
          "Bid on the emergency and the job-to-be-done — 'AC not cooling', 'burst pipe cleanup' — not just your brand name.",
          "Geo-target the suburbs you actually serve, and schedule bids around when homeowners are desperate (nights, heat waves, first freeze).",
          "Send every ad to [a page about that one job](/design-lead-pages-better-conversions) — never your homepage.",
          "Use call assets and keep the phone number in the ad copy itself.",
          "Watch [search terms daily](/blog/google-ads-and-ai) and cut the junk before it eats the budget.",
        ],
      },
      { type: "h2", text: "The receipts" },
      {
        type: "p",
        text: "One long-running client of the agency grew from roughly **$1M to $4M** in revenue with this playbook doing the heavy lifting on paid search — while the ongoing management cut their ad costs by about half through relentless search-term pruning and better quality scores. And the documented case on our site shows the same pattern: a real-estate client saw [300% more new leads in the first 60 days](/case-studies/keller-williams-realty), with 23 leads in that window against the prior two. Different industries, same mechanics — urgent intent, tight targeting, a page that answers.",
      },
      { type: "h2", text: "Where home-service owners get burned" },
      {
        type: "p",
        text: "The usual failures: bidding on their own brand and calling it leads, sending every ad to the homepage, letting broad match quietly widen the net, and — most expensive of all — nobody watching the account daily. **Ads reward attention the way a furnace rewards maintenance.**",
      },
      {
        type: "bottom",
        text: "Bottom line: for HVAC, restoration and home-service companies, Google Ads puts you in front of the exact person having the exact emergency you fix. Run it with the playbook — and if you'd rather have a specialist steering daily, that's our whole business.",
      },
    ],
  },
  {
    slug: "kansas-city-home-services-local-search",
    links: [
      { label: "Creating Website Content for KC Home Service Companies", to: "/blog/home-services-content-kansas-city", note: "Service pages and suburbs, done right" },
      { label: "Google Business Profile & Local Search", to: "/google-business-profile", note: "Win the local pack in your market" },
    ],
    category: "Local Content",
    graphic: "/img/blog/kc-home-services.svg",
    title: "Kansas City Home Services: Win the Two-State Search",
    description:
      "Kansas City is a two-state metro of dozens of suburbs — and that's exactly how you win its searches. The local game plan for KC home-service companies.",
    excerpt:
      "KC isn't one market — it's Kansas City, Overland Park, Olathe, Lee's Summit and a dozen more. Treat each suburb as its own search and the metro opens up.",
    readTime: "6 min read",
    date: "2026-10-21",
    content: [
      {
        type: "p",
        text: "Kansas City looks like one market on a map. It isn't. It's a two-state metro of dozens of distinct towns — Kansas City, Overland Park, Olathe, Lee's Summit, Independence, Shawnee, Blue Springs — and homeowners in each one search like their town is the whole world. '[Plumber near me](/blog/seo-and-local-geo)' typed in Olathe is a different search result than the same words typed in Independence. That's not a problem; that's the opening.",
      },
      { type: "h2", text: "Win the metro one suburb at a time" },
      {
        type: "ul",
        items: [
          "Your [Google Business Profile](/google-business-profile) service areas should name the towns you actually serve — specifically, not 'Kansas City area'.",
          "Build a page or section for each core service, and let your suburb coverage show naturally in the content.",
          "Reviews from named suburbs are local proof — 'fast AC repair in Lee's Summit' in a review does more than any tagline.",
          "Seasonal content wins the metro's weather: [storm season for restoration](/case-studies/tsc-restoration), first freeze for HVAC, spring for roofing.",
        ],
      },
      { type: "h2", text: "The Kansas City advantage most companies miss" },
      {
        type: "p",
        text: "Because the metro sprawls across two states, plenty of competitors market to 'Kansas City' as one blob and lose the suburbs to whoever speaks town by town. The company whose site and profile clearly say 'we serve Olathe, we serve Blue Springs, here's our work in Lee's Summit' collects the calls the blob-marketers never see. **Specificity is the whole trick** — and it costs nothing but attention.",
      },
      { type: "h2", text: "What the work looks like week to week" },
      {
        type: "p",
        text: "Keep the profile current with real project photos, earn reviews on a steady rhythm rather than in bursts, keep your service pages matching what homeowners actually type, and check what the KC-market searches are each season. None of it is complicated — all of it rewards consistency.",
      },
      {
        type: "bottom",
        text: "Bottom line: Kansas City rewards the home-service company that treats **twelve towns like twelve markets** — specific profiles, specific pages, specific proof. Do that and the metro's searches start coming to you. If you want it handled, that's our whole business.",
      },
    ],
  },
  {
    slug: "how-to-create-an-effective-google-business-profile-for-local-seo",
    links: [
      { label: "Google Business Profile & Local Search", to: "/google-business-profile", note: "Win the local pack in your market" },
      { label: "Attract More Local Customers", to: "/attract-more-local-customers", note: "The 6-step profile tune-up" },
    ],
    category: "Local Search",
    graphic: "/img/blog/gbp-home-guide.svg",
    title: "Google Business Profile Guide for Home Services",
    description:
      "Learn how to optimize your Google Business Profile with accurate details, services, reviews, photos, and tracking to help attract local customers.",
    excerpt:
      "Does your profile make it easy for a homeowner to understand what you do, where you work and how to contact you? This guide walks through the nine pieces that matter.",
    readTime: "9 min read",
    date: "2026-10-20",
    content: [
      {
        type: "p",
        text: "Does your Google Business Profile make it easy for a homeowner to understand what you do, where you work, and how to contact you? For an HVAC company, electrician, plumber, or other home-service business, those are the questions your profile should answer. Google Business Profile lets you present business information to people finding you through Google Search and Maps — and keeping that information complete and accurate can help your business appear in relevant local results.",
      },
      {
        type: "p",
        text: "Start with the essentials: accurate details, the right services, helpful photos, honest reviews, and a clear way to take the next step. This guide walks you through how to put those pieces together and measure the customer actions that follow.",
      },
      { type: "h2", text: "What Is a Google Business Profile?" },
      {
        type: "p",
        text: "Your Google Business Profile is the listing that helps customers learn about your business on Google Search and Maps. Depending on your business and the available features, it can display your contact details, hours, services, photos, and customer reviews.",
      },
      {
        type: "p",
        text: "Think about the questions a homeowner might have before contacting you:",
      },
      {
        type: "ul",
        items: [
          "Do you provide the service they need?",
          "Do you work in their area?",
          "What do previous customers say?",
          "How can they request service?",
        ],
      },
      {
        type: "p",
        text: "Use those questions to evaluate your profile. The goal is to give someone enough useful information to decide whether your business is a good fit.",
      },
      { type: "h2", text: "1. Find, Claim, and Verify Your Profile" },
      {
        type: "p",
        text: "Search Google for your business name and city before creating a new listing. If your business already has a profile, follow the appropriate steps to claim it or request ownership. If it is not listed, you can add it.",
      },
      {
        type: "p",
        text: "Google determines which verification methods are available for your business — you may see options such as video, phone or text, email, or mail. Follow the instructions Google provides rather than assuming you can choose any method, and be prepared for more than one verification step.",
      },
      {
        type: "p",
        text: "Our recommendation: keep ownership connected to an account your business controls. If someone helps manage your marketing, give them the appropriate access rather than sharing your password.",
      },
      { type: "h2", text: "2. Set Up Your Location and Service Area Correctly" },
      {
        type: "p",
        text: "Home-service businesses need to pay particular attention to how they represent their location. A service-area business travels to customers and does not serve them at its business address. A hybrid business serves customers at an eligible physical location and also travels to them.",
      },
      {
        type: "p",
        text: "If customers don't visit your business address, Google instructs you to remove that address from public display and enter your service area instead — a plumber operating from a home office, for example, shouldn't present that home as a customer-facing storefront. Choose the cities or postal codes you actually serve; Google allows up to 20 service areas and recommends keeping them specific and accurate.",
      },
      {
        type: "ul",
        items: [
          "Can we realistically serve customers throughout every area listed?",
          "Does our website describe the same coverage?",
          "Are we displaying an address where customers are actually welcome?",
        ],
      },
      { type: "h2", text: "3. Make Your Core Business Information Accurate" },
      {
        type: "p",
        text: "Review your business name, phone number, website, hours, and categories. Use your real-world business name — don't add extra city names or service keywords unless they're genuinely part of it; Google requires the name to reflect how the business is represented in the real world. Choose categories that accurately describe your business, keep your hours current, and include special hours when appropriate.",
      },
      {
        type: "ul",
        items: [
          "Call the listed number to confirm it reaches the right person or system.",
          "Open the website link on a phone, and confirm your contact form works.",
          "Check that the hours accurately represent your availability.",
          "Compare the details with your website and other business listings.",
          "Only advertise emergency or around-the-clock availability if you can deliver it.",
        ],
      },
      { type: "h2", text: "4. Describe the Services You Actually Provide" },
      {
        type: "p",
        text: "Where the services editor is available, Google lets you select suggested services or add custom ones, with descriptions and pricing details. Services may be highlighted when customers search for something your business offers — so be specific enough to help a homeowner recognize the service they need.",
      },
      {
        type: "p",
        text: "For example, an electrical contractor might list electrical panel replacement, EV charger installation, outlet and circuit repair, generator installation, and electrical troubleshooting. An HVAC contractor might list furnace repair, air conditioning installation, heat pump service, and seasonal maintenance. These are examples — include only services your company provides.",
      },
      {
        type: "p",
        text: "For descriptions, we recommend plain language that explains what the service covers. Avoid repeating the same phrase or city name throughout every entry — write for the customer who is trying to understand whether you can solve their problem.",
      },
      { type: "h2", text: "5. Add Photos That Show Your Work" },
      {
        type: "p",
        text: "Google supports photos and videos that show customers what your business offers. For a home-service company, we recommend using real images of:",
      },
      {
        type: "ul",
        items: [
          "Your team",
          "Branded vehicles",
          "Technicians working safely",
          "Completed installations",
          "Before-and-after projects",
          "Your customer-facing location, if applicable",
        ],
      },
      {
        type: "p",
        text: "Choose images that represent your business accurately — obtain permission before sharing customer property, and check that photos don't expose private information. The simple test: does this image help a homeowner understand your work or recognize the people arriving at their door?",
      },
      { type: "h2", text: "6. Request Honest Reviews and Respond Thoughtfully" },
      {
        type: "p",
        text: "Google reviews give prospective customers information about other people's experiences with your business. You can share a review link or QR code to make leaving feedback easier. Ask for an honest review — **Google prohibits incentives such as discounts or free services in exchange for reviews.** A straightforward request: \"Thank you for choosing us. If you'd like to share your experience, we'd appreciate an honest Google review. Here's the link.\"",
      },
      {
        type: "p",
        text: "When replying, keep the response relevant to the customer's feedback: thank people for useful comments, address concerns professionally, and protect their privacy. For complicated issues, invite the customer to contact you directly. Make review requests a consistent part of your follow-up process rather than waiting until someone remembers to ask.",
      },
      { type: "h2", text: "7. Share Useful Updates and Offers" },
      {
        type: "p",
        text: "Business Profile posts can share announcements, offers, and business updates — depending on the post type, they can include images and a button linking to a relevant webpage. For home-service businesses, useful topics might include:",
      },
      {
        type: "ul",
        items: [
          "A seasonal maintenance offer",
          "A recently completed project",
          "A new service",
          "Preparation tips before severe weather",
          "A helpful article answering a common customer question",
        ],
      },
      {
        type: "p",
        text: "Give each post one clear purpose — if you promote an air conditioning tune-up, link to a page explaining that service and how to request it. Choose a manageable publishing schedule and prioritize useful information over repetitive promotional posts.",
      },
      { type: "h2", text: "8. Understand What Influences Local Rankings" },
      {
        type: "p",
        text: "Google identifies three main considerations for local results:",
      },
      {
        type: "ul",
        items: [
          "Relevance: how well your business matches the search.",
          "Distance: how far your business is from the person searching.",
          "Prominence: how well-known your business is, including signals such as links and reviews.",
        ],
      },
      {
        type: "p",
        text: "**Google also states that businesses cannot request or pay for a better local ranking.** Use that guidance to set realistic expectations: focus on accurate information, clear services, genuine customer feedback, and [a useful website](/website-optimization) — and avoid treating any single profile edit as a guaranteed route to the top of Maps.",
      },
      { type: "h2", text: "9. Measure Customer Actions and Booked Work" },
      {
        type: "p",
        text: "Open your verified profile's Performance section to review what's available for your business — this can include views, search terms, call-button clicks, website clicks, and direction requests. Pay attention to what each metric means: Google's \"calls\" metric counts clicks on the call button; it does not confirm that someone reached your team, became a qualified lead, or booked a job. Performance reporting can also include activity associated with [Google Ads](/ad-remarketing).",
      },
      {
        type: "p",
        text: "We recommend comparing profile activity with your own business records:",
      },
      {
        type: "ul",
        items: [
          "Profile views and search terms — how are people discovering us?",
          "Call-button and website clicks — are people taking a next step?",
          "Actual inquiries — how many people contacted us?",
          "Qualified leads — were those inquiries a good fit?",
          "Booked jobs — how much work resulted?",
        ],
      },
      {
        type: "p",
        text: "Record lead sources in your CRM or job-management system where possible. Ask new customers how they found you, and review the answers alongside your reporting — that gives you a more useful picture than judging success by profile views alone.",
      },
      { type: "h2", text: "A Practical Google Business Profile Maintenance Checklist" },
      {
        type: "p",
        text: "We recommend using this checklist as a recurring review:",
      },
      {
        type: "ul",
        items: [
          "Confirm your phone number and website link work.",
          "Check regular hours and upcoming holiday hours.",
          "Review your services and service areas.",
          "Look for inaccurate or unexpected profile changes.",
          "Respond to customer reviews that need a reply.",
          "Add relevant recent project photos.",
          "Publish a useful update when you have something to share.",
          "Review profile performance alongside inquiries and booked jobs.",
          "Assign responsibility to one person so important updates do not get overlooked.",
        ],
      },
      { type: "h2", text: "Need Help Improving Your Google Business Profile?" },
      {
        type: "p",
        text: "Start with the basics: correct information, clearly described services, honest customer feedback, and an easy way to contact your business.",
      },
      {
        type: "bottom",
        text: "If you would like help reviewing your profile and deciding what to improve, explore our [Google Business Profile optimization services](/google-business-profile) — or [contact ZD Design Agency](/contact) to discuss your profile and next steps.",
      },
    ],
  },
  {
    slug: "generative-engine-optimization-how-we-do-it-at-zd-design",
    links: [
      { label: "AI Visibility — the $397/mo founding program", to: "/ai-visibility", note: "The full program, founding rate" },
      { label: "How to Show Up in AI Answers", to: "/blog/how-to-show-up-in-ai-answers", note: "The DIY version of these steps" },
      { label: "What Is GEO, How Does It Work? Do I Need It?", to: "/blog/what-is-geo-how-does-it-work", note: "What GEO is before how" },
    ],
    category: "AI Search",
    graphic: "/img/blog/geo-at-zd.svg",
    title: "Generative Engine Optimization: How We Do It at ZD Design",
    description:
      "Inside ZD Design's GEO program: crawler access, published unique content, authority building and mention tracking — the exact process that gets you named in AI answers.",
    excerpt:
      "No mystery, no magic — the exact four-step process we run inside our AI Visibility program: access, content, authority, measurement.",
    readTime: "6 min read",
    date: "2026-10-01",
    content: [
      {
        type: "p",
        text: "Most agencies sell GEO as a mystery — proprietary voodoo they can't show you. We sell it as a **program**: four steps, run every month, with the results in a dashboard you can open anytime. Here's exactly how we do **Generative Engine Optimization** at ZD Design — the same process inside our [AI Visibility program](/ai-visibility), step by step, nothing held back.",
      },
      { type: "h2", text: "Step 1 — Open the door for the crawlers" },
      {
        type: "p",
        text: "Before anything else we make sure AI engines are **allowed** to read you: robots.txt rules that welcome every major AI crawler, an **llms.txt** file that points engines at your best pages, and server-side rendering so bots that don't run JavaScript still see every word. You'd be surprised how many websites quietly block their own recommenders — [our step-by-step guide](/blog/how-to-show-up-in-ai-answers) shows you how to check yours in ten minutes.",
      },
      { type: "h2", text: "Step 2 — Publish content worth quoting" },
      {
        type: "p",
        text: "Engines quote sentences, not slogans. We publish **unique, specific, answer-shaped pages** — real questions your customers ask, answered plainly, with your services and service areas stated in the first paragraph. Not thin AI-spun filler: engines discount it, and customers can smell it. [What GEO actually is](/blog/what-is-geo-how-does-it-work) explains why quotable beats clever every time.",
      },
      { type: "img", src: "/img/blog/geo-at-zd.svg", alt: "The four steps of ZD Design's GEO program: access, content, authority, measure" },
      { type: "h2", text: "Step 3 — Build authority beyond your site" },
      {
        type: "p",
        text: "AI engines hedge toward sources the rest of the web confirms. So we build the proof outside your walls too: **backlinks from real directories and partners**, a complete [Google Business Profile](/google-business-profile) with review velocity, and consistent facts everywhere engines cross-check. A restoration client's authority work alone lifted [domain authority 20% in six months](/case-studies/floodmasters) — the trust layer that makes engines comfortable naming you.",
      },
      { type: "h2", text: "Step 4 — Measure mentions like rankings" },
      {
        type: "p",
        text: "You can't improve what you don't track. We watch **when and how you're named** in ChatGPT, Gemini and Claude — which answers include you, which competitors they quote instead — and it all lands in the same [monthly dashboard](/ai-visibility) as your Google rankings. One place, both engines, every month.",
      },
      { type: "h2", text: "What it costs" },
      {
        type: "p",
        text: "The full program — both engines, all four steps, run monthly — is **$597/mo**, month-to-month, cancel anytime. Founding clients [lock $397/mo](/ai-visibility) while spots last. If you'd rather run the steps yourself first, the [free AI-answers checklist](/ai-answer-checklist) is the whole playbook in shareable form.",
      },
      {
        type: "bottom",
        text: "Bottom line: our GEO is a **repeatable program — access, content, authority, measurement** — not a mystery. Run the checklist yourself, or hand us the wheel and watch the dashboard.",
      },
    ],
  },
  {
    slug: "7-email-capture-opt-tips-improve-conversions",
    links: [
      { label: "How to Design Lead Pages for Better Conversions", to: "/design-lead-pages-better-conversions", note: "The page behind the opt-in" },
      { label: "Marketing Strategy & Consulting", to: "/coaching-consulting", note: "A plan built around your list" },
    ],
    category: "Email Marketing",
    graphic: "/img/blog/leads-social-hub.svg",
    title: "7 Email Opt-in Tips That Improve Conversions",
    description:
      "Seven opt-in fixes that lift conversions: benefit-driven form copy, one clear goal, matched offers, single-field forms and welcome pages that warm visitors up.",
    excerpt:
      "Your email list is the only audience you own. These seven opt-in improvements — copy, friction, offers and forms — turn more visitors into subscribers.",
    readTime: "6 min read",
    date: "2026-10-20",
    content: [
      {
        type: "p",
        text: "**Your email list is the one marketing asset nobody can take from you** — algorithms can't throttle it, platforms can't lock it. Every visitor who opts in is a relationship you keep. These are the seven opt-in improvements that reliably lift conversions.",
      },
      { type: "h2", text: "1) Say exactly what they get" },
      {
        type: "p",
        text: "Vague opt-ins convert badly. A headline that names the payoff — 'Get Your Free Content Marketing Toolkit' — plus a call-to-action button that says the benefit ('Get the Toolkit') instead of a generic 'Submit' will out-convert clever-but-unclear copy every time. Test variants against each other and keep the winner.",
      },
      { type: "h2", text: "2) One page, one goal" },
      {
        type: "p",
        text: "[Pages built to capture emails](/design-lead-pages-better-conversions) should do only that. Strip the navigation bars, headers and links that invite people to wander off, put the opt-in box above the fold, and let nothing compete with the single action you want.",
      },
      { type: "h2", text: "3) Earn the email with proof" },
      {
        type: "p",
        text: "Confidence converts. [Testimonials](/testimonials), reviews and short success stories next to the form answer the silent question — 'is this worth my address?' — before it's asked.",
      },
      { type: "h2", text: "4) Only ask when it makes sense" },
      {
        type: "p",
        text: "Match the ask to the offer: an email series or a substantial resource justifies an opt-in; forcing one before a simple video or a one-page PDF creates friction and suspicion. If the content doesn't need the email, don't demand it — save the ask for when it's earned.",
      },
      { type: "h2", text: "5) Reduce the effort to one field" },
      {
        type: "p",
        text: "**Every extra field costs subscribers.** Ask for the email alone; names, phone numbers and addresses on a first opt-in reliably tank conversion rates. You can learn more about them after they trust you.",
      },
      { type: "img", src: "/img/blog/leads-social-hub.svg", alt: "Illustration of a signup form connected to email, reviews and social channels" },
      { type: "h2", text: "6) Welcome paid traffic with a splash, not a shove" },
      {
        type: "p",
        text: "Visitors arriving from [ads or social links](/facebook-ads) land warm — give them a dedicated welcome page with the offer front and center, plus an explicit way to decline and browse the main site. And skip the out-of-nowhere pop-ups; they feel intrusive and drive bounce rates up.",
      },
      { type: "h2", text: "7) Deliver in small, useful bites" },
      {
        type: "p",
        text: "A massive download sits unopened. Content delivered in small, quickly-implementable pieces gets used — and used content builds the trust that keeps subscribers opening, clicking and eventually buying.",
      },
      {
        type: "bottom",
        text: "Bottom line: an opt-in is a trade — value for trust. Say exactly what they get, strip every friction, ask for one field, and deliver in useful bites. Do that and your list grows on autopilot. If you want the whole funnel built right, that's our whole business.",
      },
    ],
  },
  {
    slug: "is-your-website-mobile-ready-why-your-mobile-site-needs-to-be-optimized-for-conversion",
    links: [
      { label: "Website Design & Optimization", to: "/website-optimization", note: "Fast, mobile-first pages that convert" },
      { label: "Top 10 Ways to Improve Your Website User Experience", to: "/top-10-ways-to-improve-your-website-user-experience", note: "The experience fixes that compound" },
    ],
    category: "Mobile",
    graphic: "/img/blog/leads-growth.svg",
    title: "Is Your Website Mobile Ready? Speed + Conversion",
    description:
      "Most of your customers meet you on a phone. The mobile speed and conversion essentials — one responsive site, thumb-friendly pages, and content that loads fast.",
    excerpt:
      "Your customers meet you on phones — and Google knows it. Why one fast, responsive site beats the old 'separate mobile site', and the fixes that lift mobile conversions.",
    readTime: "5 min read",
    date: "2026-10-20",
    content: [
      {
        type: "p",
        text: "For most local businesses, the majority of visitors — and nearly all of the urgent ones — arrive on a phone. A huge share of social traffic is mobile too. If your site isn't fast and effortless on a small screen, you're losing the customers who matter most. Here's what mobile-ready means today.",
      },
      { type: "h2", text: "One site — not a separate mobile version" },
      {
        type: "p",
        text: "The old advice was to build a separate 'mobile site' — a stripped-down m.yoursite.com twin. That era is over. Today the answer is [one responsive website](/website-optimization): the same content and URLs, with a layout that adapts to any screen. You maintain one site, Google indexes one site, and nothing falls out of sync — separate mobile versions now **create duplicate-content headaches and split your rankings**.",
      },
      { type: "h2", text: "Speed is the conversion killer" },
      {
        type: "p",
        text: "**Mobile visitors are the least patient audience you have.** Slow-loading graphics, heavy scripts and oversized images send them straight back to the results. Keep the design lean — CSS-driven layouts, compressed images, minimal bloat — and test your pages on a real phone, on cellular data, not just your desktop.",
      },
      { type: "h2", text: "Design for thumbs and quick answers" },
      {
        type: "ul",
        items: [
          "Put what mobile visitors need first: your phone number, directions, and the service list — not a slideshow.",
          "Use [navigation that works](/top-10-ways-to-improve-your-website-user-experience) on small viewports, with text that stays readable at reduced scale.",
          "Make tap targets big and forms short — thumbs are clumsy and patience is thin.",
          "Start the mobile layout at design time, not as an afterthought — retrofitting shifts everything.",
        ],
      },
      { type: "img", src: "/img/blog/signs-refresh-tools.svg", alt: "Illustration of tools tuning a web page" },
      { type: "h2", text: "How to check yourself" },
      {
        type: "p",
        text: "Run your pages through Google's PageSpeed Insights and Chrome's device mode, and hand your phone to someone outside your business — watch where they hesitate. Fix what slows the first screen and whatever stands between the visitor and the call.",
      },
      {
        type: "bottom",
        text: "Bottom line: mobile isn't a side channel — it's where your customers are. One fast, responsive site with thumb-friendly pages and quick answers is the standard. If your site fails that test, our website optimization work exists precisely for that.",
      },
    ],
  },
  {
    slug: "how-to-use-facebook-custom-audiences-in-power-editor-for-ad-targeting",
    links: [
      { label: "Facebook & Instagram Ads", to: "/facebook-ads", note: "Reach the audiences ready to buy" },
      { label: "10 Ways to Screw Up Your Online Ad Campaign", to: "/10-ways-to-screw-up-your-online-ad-campaign", note: "The mistakes to avoid" },
    ],
    category: "Remarketing",
    graphic: "/img/blog/leads-social-hub.svg",
    title: "Facebook Custom Audiences: The Current Playbook",
    description:
      "The modern way to build Facebook audiences: customer lists, site visitors and lookalikes — a current take on the old Power Editor workflow, plus the plan.",
    excerpt:
      "Power Editor is long gone, but custom audiences are stronger than ever. The current workflow — lists, pixels, lookalikes — plus how to plan and define your audience.",
    readTime: "6 min read",
    date: "2026-10-20",
    content: [
      {
        type: "p",
        text: "Years ago, Facebook ad targeting meant a tool called Power Editor. That tool is long retired — everything now lives in Meta Ads Manager — but the idea behind it is more powerful than ever: instead of bidding at strangers, you aim your ads at people who already know you, and at people who look just like them. Here's the current playbook.",
      },
      { type: "h2", text: "Custom audiences: target people who already know you" },
      {
        type: "ul",
        items: [
          "Customer lists: upload emails or phone numbers of [customers who opted in](/7-email-capture-opt-tips-improve-conversions) — Meta matches them to accounts. CSV exports from your email platform work fine.",
          "Website visitors: with your Meta pixel installed, you can [retarget anyone who visited](/blog/google-remarketing-adwords-and-adroll), or just the ones who hit pricing and left.",
          "Engagers: people who interacted with your page, posts or videos — warm, cheap, and easy to convert.",
        ],
      },
      { type: "h2", text: "Lookalike audiences: find everyone who looks like them" },
      {
        type: "p",
        text: "Take your best custom audience — buyers, not just subscribers — and let Meta find people who share their traits. **Lookalikes are how a small customer list becomes a full funnel**: the closer the source list is to your ideal customer, the better the match.",
      },
      { type: "h2", text: "Use Audience Insights to sharpen the picture" },
      {
        type: "p",
        text: "Meta's Audience Insights shows the demographics and interests of the audience you've built — age ranges, locations, the pages they like. Use it to refine your creative and targeting, and to spot adjacencies worth testing.",
      },
      { type: "img", src: "/img/blog/signs-refresh-tools.svg", alt: "Illustration of refining an audience with data" },
      { type: "h2", text: "Planning beats improvising" },
      {
        type: "p",
        text: "None of this works without a plan. A workable social media plan in six steps: set one measurable goal per campaign; know exactly who you're targeting (build 1-3 audience personas and define them by demographics, interests and pain points); pick the platforms where those people actually are; plan the content mix — useful, human, and occasionally promotional; decide the budget and the measurement; then review monthly and reallocate toward what worked.",
      },
      {
        type: "bottom",
        text: "Bottom line: the tools changed their names but the advantage is the same — advertise to people who know you, and to people who look exactly like them. Build the list, install the pixel, plan the calendar. If you'd rather have it run for you, our [Facebook & Instagram Ads service](/facebook-ads) is that job.",
      },
    ],
  },
  {
    slug: "8-steps-to-evaluate-your-product-or-service-using-competitive-analysis",
    links: [
      { label: "Marketing Strategy & Consulting", to: "/coaching-consulting", note: "A plan built around your edge" },
      { label: "How Much SEO Do I Need for My Small Business?", to: "/much-seo-need-small-business", note: "The honest answer on effort vs. outcome" },
    ],
    category: "Strategy",
    graphic: "/img/blog/leads-social-hub.svg",
    title: "Evaluate Your Product: 8 Competitive Analysis Steps",
    description:
      "Your competitors already spent the money learning what works. Eight steps to evaluate your product against theirs — and claim the edge they're leaving open.",
    excerpt:
      "Competitive analysis isn't copying — it's learning for free from rivals who already paid for the lessons. Eight steps to find your edge and claim it online.",
    readTime: "5 min read",
    date: "2026-10-20",
    content: [
      {
        type: "p",
        text: "As a small business owner you wear many hats — and one of them is watching the competition. Competitive analysis is simply the discipline of determining the strengths and weaknesses of the competitors in your market so you can build strategies with a distinct advantage. Your competitors have already spent real money learning what works. Learn from their spending instead of repeating it.",
      },
      { type: "h2", text: "The 8 steps to find your competitive edge" },
      {
        type: "ul",
        items: [
          "Review your top three competitors' websites — homepage and sales pages. Do they work? Why?",
          "Look at how they sell: [paid ads](/ad-remarketing), social, or organic traffic? That mix tells you where the money flows.",
          "Ask the blunt question: are you better than the rest? What genuinely sets you apart online?",
          "Study what kind of website they run and how they use it — brochure, machine, or museum.",
          "Compare how you educate and inform customers — blog, email, social — and how well it works.",
          "Hone in on your value-added difference: the thing you offer the others don't.",
          "Watch how the competition uses [content to build domain authority](/fresh-content) — then do it better.",
          "Check their links and citations, and build authority from the same worthwhile sources.",
        ],
      },
      { type: "img", src: "/img/blog/signs-refresh-tools.svg", alt: "Illustration of a magnifying glass studying a competitor's web page" },
      { type: "h2", text: "Why this works" },
      {
        type: "p",
        text: "Look at what competitors do right and wrong, and you skip years of trial and error. Build your online strategy around the tactics already proving themselves in your market — website, ads, content — then differentiate where they're weak. **That's how you gain market share quickly without out-spending anyone.**",
      },
      {
        type: "bottom",
        text: "Bottom line: your competitors' websites and ads are a free education — audit the top three, find the gap, and claim it. If you want that analysis done properly and [turned into a plan](/coaching-consulting), that's our whole business.",
      },
    ],
  },
  {
    slug: "website-ranking-factors-you-need-to-know-about",
    links: [
      { label: "SEO & Google Visibility", to: "/seo", note: "Own the searches that matter" },
      { label: "Why Fresh Content Is Key to SEO Success", to: "/fresh-content", note: "The routine that keeps you ranking" },
    ],
    category: "SEO",
    graphic: "/img/blog/fresh-content-growth.svg",
    title: "Website Ranking Factors You Need to Know About",
    description:
      "What actually moves rankings today: helpful content, page experience, links, local signals — and how AI search changed the picture. Plain English, no myths.",
    excerpt:
      "Ranking factors stop being a mystery once you group them: content, experience, links and local signals. Here's what matters now — and what stopped mattering years ago.",
    readTime: "7 min read",
    date: "2026-10-20",
    content: [
      {
        type: "p",
        text: "Every couple of years the SEO industry gets a fresh round of ranking-factor studies, and specialists debate the findings in detail. Useful — but business owners don't need a correlation chart. You need to know which levers exist, which ones you control, and which 'techniques' stopped working years ago. Here's the picture today, in plain English.",
      },
      { type: "h2", text: "Factor 1 — Helpful, substantial content" },
      {
        type: "p",
        text: "The factor that has only grown stronger: pages with genuinely helpful, in-depth content outrank thin ones. Higher-ranking pages tend to carry more substance — but substance means useful, not filler. Write for the reader's actual question, cover it fully, and keep it current. Since the arrival of AI Overviews and assistants like ChatGPT and Gemini, being the clearest, most complete answer matters more than ever — those systems [quote the best source, not the loudest](/blog/what-is-geo-how-does-it-work).",
      },
      { type: "h2", text: "Factor 2 — On-page fundamentals" },
      {
        type: "p",
        text: "The basics still carry weight, and they're all in your control: the title tag (with the important words near the front), your headings, the URL itself, and naturally used keywords across the page. Keyword stuffing died years ago — keywords belong in natural places (title, subheads, body, image alt text) and nowhere they sound awkward.",
      },
      { type: "h2", text: "Factor 3 — Links, quality over tricks" },
      {
        type: "p",
        text: "Links from real, relevant websites still influence rankings — more unique, relevant sources pointing at you is better. But the mechanics changed: 'built' links from unrelated sites carry little weight and can earn penalties. Relevance beats raw authority — a handful of links from sites in your industry outperforms hundreds from random ones. And a natural link profile includes a mix, so chase relationships and content worth citing, not link schemes.",
      },
      { type: "h2", text: "Factor 4 — Page experience" },
      {
        type: "p",
        text: "Speed, mobile-friendliness, stability and a site that's easy to use — Google measures all of it. The old advice holds: short, clean URLs; fast load times; a layout that works on the phone in your customer's hand. A positive experience is part of ranking, not a luxury after it.",
      },
      { type: "img", src: "/img/blog/fresh-cornerstone.svg", alt: "Illustration of articles linking to a central cornerstone page" },
      { type: "h2", text: "Factor 5 — Local and freshness signals" },
      {
        type: "p",
        text: "For small businesses, **local signals dominate**: your [Google Business Profile](/google-business-profile), reviews, citations and location pages decide the map pack. And **freshness is a standing signal** — regularly updated sites get crawled more, indexed faster and trusted longer. Personalized and local results also mean 'rank #1' matters less than 'visible in the results your customer sees' — which is why the work aims at queries, not vanity positions.",
      },
      { type: "h2", text: "The myths worth retiring" },
      {
        type: "ul",
        items: [
          "'SEO can be done cheaply' — it can be done efficiently, but bargain SEO delivers what you paid for.",
          "'More links from big-name sites' — irrelevant authority loses to relevant relevance, every time.",
          "'It's a one-time setup' — rankings drift as algorithms and competitors move; the sites that keep working keep winning.",
          "'SEO is tricks' — [modern SEO](/seo) is a long-term strategy: identify the keywords that convert, build the experience, earn the links, keep it fresh.",
        ],
      },
      {
        type: "bottom",
        text: "Bottom line: ranking today comes from four controllable levers — helpful content, clean on-page work, relevant links and a fast local-friendly experience — kept fresh over time. Do those and the rankings follow. If you'd rather have a specialist pull them for you, that's our whole business.",
      },
    ],
  },
  {
    slug: "a-301-vs-a-302-redirect",
    links: [
      { label: "SEO & Google Visibility", to: "/seo", note: "Technical SEO handled for you" },
      { label: "Cross-Web Repair Program", to: "/cross-web-repair", note: "Fix the gaps everywhere at once" },
    ],
    category: "SEO",
    graphic: "/img/blog/signs-content-update.svg",
    title: "A 301 vs. a 302 Redirect (And When to Use Each)",
    description:
      "301s pass rankings to a new URL; 302s say 'temporarily moved'. The difference, plus canonical tags and meta refreshes — redirect knowledge every owner needs.",
    excerpt:
      "Pick the wrong redirect and you can hand your rankings away. Here's the plain-English difference between 301s, 302s, canonical tags and meta refreshes.",
    readTime: "4 min read",
    date: "2026-10-20",
    content: [
      {
        type: "p",
        text: "Redirects look interchangeable from the outside — the visitor lands in the right place either way. But search engines treat them very differently, and choosing the wrong one can quietly hand your rankings away. Here's the difference.",
      },
      { type: "h2", text: "The 301 — permanent" },
      {
        type: "p",
        text: "A 301 tells browsers and search engines a page has moved permanently. Google passes the old URL's [link equity and ranking signals](/blog/website-ranking-factors-you-need-to-know-about) to the new one, and the new URL replaces the old in results. Renaming a page, consolidating articles, moving domains — **this is the one you want.**",
      },
      { type: "h2", text: "The 302 — temporary" },
      {
        type: "p",
        text: "A 302 says the move is temporary and the original URL will return. Search engines generally keep the ORIGINAL URL indexed (exceptions exist), so the 302 is right for short-lived situations — a seasonal page swap or a brief maintenance detour. **Use a 302 when you mean to change back; use a 301 when you don't.**",
      },
      { type: "h2", text: "The canonical tag — a suggestion, not a redirect" },
      {
        type: "p",
        text: "A canonical tag lives in a page's header and suggests which URL is the 'original' when several pages carry the same or very similar content. Unlike a redirect, the visitor stays where they are — it only tells search engines which version to index. Essential for filtered catalogs, printer-friendly duplicates and parameter-laden URLs.",
      },
      { type: "img", src: "/img/blog/signs-refresh-tools.svg", alt: "Illustration of tools auditing a web page" },
      { type: "h2", text: "The meta refresh — the last resort" },
      {
        type: "p",
        text: "A meta refresh is an HTML-level instruction to send the browser elsewhere after a delay. Short ones (a few seconds, especially zero) are mostly treated like permanent redirects; longer ones may be indexed as normal pages. It works — but **server-side 301s are cleaner, faster and more reliable**. Use meta refresh only when you can't touch the server.",
      },
      {
        type: "bottom",
        text: "Bottom line: moving permanently? 301. Coming back soon? 302. Duplicate content? Canonical tag. Stuck with HTML only? A zero-second meta refresh. Match the redirect to the intent and your rankings stay intact — and if a migration is on your horizon, [talk to us before it ships](/contact), not after.",
      },
    ],
  },
  {
    slug: "website-looking-dated-5-key-elements-for-a-redesign",
    links: [
      { label: "Cross-Web Repair Program", to: "/cross-web-repair", note: "For sites past the patch stage" },
      { label: "Website Design & Optimization", to: "/website-optimization", note: "A faster site that converts" },
    ],
    category: "Website Care",
    graphic: "/img/blog/wordpress-updates.svg",
    title: "Website Looking Dated? 5 Key Elements for a Redesign",
    description:
      "Dated, tired and quietly leaking leads? The five key elements to get right in a website redesign — budget, designer, quotes, timeline and sign-off.",
    excerpt:
      "Gone are the days when just having a web presence was enough. If your site looks tired and leaks leads, here's how to run a redesign that pays for itself.",
    readTime: "6 min read",
    date: "2026-10-20",
    content: [
      {
        type: "p",
        text: "Is your company website dated, tired-looking, or just [not bringing in leads](/double-your-leads-in-just-3-months)? In today's online economy, a website is often the first experience a new customer has with your business — and gone are the days when simply having a web presence was enough. **You're literally competing for customers based on your website.** Here are the five key elements to get right in a redesign.",
      },
      { type: "h2", text: "1) Determine your budget — honestly" },
      {
        type: "p",
        text: "[Web design](/website-optimization) and redevelopment can run small or large depending on your vision. Establish a realistic budget before you start: it lets a designer offer you the most cost-effective options. Be upfront — if your vision is bigger than your budget, a good designer will propose a phased approach and tell you which items will have the biggest impact first.",
      },
      { type: "h2", text: "2) Find the right designer or developer" },
      {
        type: "p",
        text: "Ask for referrals and review portfolios until the work and style align with your vision. Know the difference between a freelancer and a firm — freelancers can be excellent, but many aren't available during business hours. Then meet your shortlist by phone or video: you'll quickly sense whether they understand your needs and whether it's a good fit.",
      },
      { type: "h2", text: "3) Get comparable quotes" },
      {
        type: "p",
        text: "Give every potential designer the same brief — the changes you want, plus a handful of sites whose look and feel you like — so the quotes are apples to apples. Ask for references from previous projects and actually check them; that's how you learn whether they hold a timeline and a budget.",
      },
      { type: "h2", text: "4) Agree on a timeline in writing" },
      {
        type: "p",
        text: "Once you've chosen, get the timeline in writing and ask for an updated wireframe early in the process so you can see exactly what the changes will entail. A sign-off process for each element is what makes sure you receive what you envisioned.",
      },
      { type: "img", src: "/img/blog/signs-refresh-tools.svg", alt: "Illustration of design tools refining a web page" },
      { type: "h2", text: "5) Handle design sign-off with honesty" },
      {
        type: "p",
        text: "Design is subjective, so a good designer will mock up the homepage and key pages for approval before building everything. Say plainly what you like and don't — that's your chance to steer. And know how many rounds of revisions are included in the project so the scope never surprises your budget.",
      },
      {
        type: "bottom",
        text: "Bottom line: a well-designed, easy-to-navigate website converts visitors into customers — a dated one hands them to your competitor. Take an honest look at your site; if it's due, run the redesign on these five elements. And if the site's too far gone to patch, that's literally our [Cross-Web Repair Program](/cross-web-repair).",
      },
    ],
  },
  {
    slug: "top-10-ways-to-improve-your-website-user-experience",
    links: [
      { label: "Website Design & Optimization", to: "/website-optimization", note: "A faster site that converts" },
      { label: "Website Looking Dated? 5 Key Elements for a Redesign", to: "/website-looking-dated-5-key-elements-for-a-redesign", note: "When tweaks aren't enough" },
    ],
    category: "Website Care",
    graphic: "/img/blog/leads-social-hub.svg",
    title: "Top 10 Ways to Improve Your Website User Experience",
    description:
      "Ten practical ways to improve your website's user experience — navigation, goals, desk testing, style guides and the persona work that makes every page land.",
    excerpt:
      "A great website converts; a frustrating one leaks. These are the ten user-experience improvements we walk clients through, in plain English.",
    readTime: "7 min read",
    date: "2026-10-20",
    content: [
      {
        type: "p",
        text: "A great website converts better than a confusing one with weak calls to action — that part's obvious. What's less obvious is how many small, fixable things decide which one yours is. Here are the ten improvements that move the needle most.",
      },
      {
        type: "ul",
        items: [
          "Make the logo clickable to Home. It's the current standard — retire the separate home button.",
          "Make your navigation rock: readable size, strong contrast, and custom terms that carry your brand's voice instead of generic labels.",
          "Determine the goals of the site. A call? A transaction? An email for follow-up? Write the goals down, talk them through with someone, then build [calls to action that match](/design-lead-pages-better-conversions).",
          "Desk-test it. Sit with someone who isn't web-savvy — ideally your ideal customer — and have them use the site while you listen and take notes. You'll walk away with dozens of fixes; fixing them can **lift the experience 30-40% or more**.",
          "Create a visual style guide: colors, fonts, imagery, logo use — one standard so branding and messaging stay consistent everywhere.",
          "Decide the type of experience you're offering: transactional, informational or navigational — and design for it deliberately.",
          "Make it easy to find stuff. The easier the find, the happier the visitor — nobody smiles at a wall.",
          "Make the site friendly. Voice matters — small phrases with personality make people feel part of the experience instead of processed by it.",
          "Match the [search intent](/blog/google-ads-and-ai) your visitors arrive with, and route each kind through a focused path. Your analytics will show you what they wanted.",
          "Create user personas: one to three sketches of exactly who you're selling to. **Write to them, not to everyone — and not to nobody.**",
        ],
      },
      {
        type: "bottom",
        text: "Bottom line: user experience isn't decoration — it's the difference between a site that leaks and a site that sells. Fix the ten, starting with the desk test, and the improvements compound across every page you own.",
      },
    ],
  },
  {
    slug: "design-lead-pages-better-conversions",
    links: [
      { label: "Website Design & Optimization", to: "/website-optimization", note: "Pages built to convert, not just look good" },
    ],
    category: "Conversions",
    graphic: "/img/blog/leads-social-hub.svg",
    title: "How to Design Lead Pages for Better Conversions",
    description:
      "Lead pages fail on friction and distraction, not luck. The design rules that lift conversions — one goal, one path, a headline that earns the email.",
    excerpt:
      "A lead page has one job: earn the email. Here's how to design for that single goal — clear headline, no exits, zero friction — so more visitors actually opt in.",
    readTime: "6 min read",
    date: "2026-10-20",
    content: [
      {
        type: "p",
        text: "A lead page is a landing page with one job: [collecting leads](/7-email-capture-opt-tips-improve-conversions), usually email addresses. Everything about its design either works toward that single exchange — value for contact — or quietly works against it. Design any objective-based page by starting in your visitor's shoes. Here's what they're thinking, and how the page should answer.",
      },
      { type: "h2", text: "Start with the one goal" },
      {
        type: "p",
        text: "A landing page exists to sell one thing — a product, a booking, a download. That specificity is its power. Design for that goal alone: fast, guided, one pathway. When visitors never have to think about what to do next, click-through and conversions take care of themselves.",
      },
      { type: "h2", text: "'I don't want your spam'" },
      {
        type: "p",
        text: "Nobody parts with an email for nothing — the offer has to be worth the privacy. Whatever you're giving in return, make its value obvious on the page, so the decision to opt in takes no second-guessing.",
      },
      { type: "h2", text: "'I don't want this to take long'" },
      {
        type: "p",
        text: "They're already there for a reason — don't add steps. Every extra page, click or field between them and the content is one more moment to reconsider. Shorten the path until it's a single motion.",
      },
      { type: "h2", text: "'I don't want to read a novel'" },
      {
        type: "p",
        text: "Keep it simple and move them through the classic flow: Awareness — catch attention. Interest — highlight what's in it for them. Desire — make them want it. Action — the exchange. That's the whole page.",
      },
      { type: "img", src: "/img/blog/signs-refresh-tools.svg", alt: "Illustration of a magnifying glass and wrench refining a web page" },
      { type: "h2", text: "Five design rules that do the heavy lifting" },
      {
        type: "ul",
        items: [
          "Start with the headline: it's how visitors know why they're here — and why they should stay rather than go back to the results.",
          "Use the subheading to seal it: one line that confirms they've come to the right place.",
          "**Eliminate the navigation: every menu link is an exit.** If the page works, they'll come back for the rest of your site on their own.",
          "Let images illustrate the offer: relevant, inviting visuals beat paragraphs — anything else just distracts from the goal.",
          "Customize the submit button: 'Submit' wastes the moment. **Make it say the payoff — 'Get the free checklist' — and make it impossible to miss.**",
        ],
      },
      { type: "h2", text: "'I don't want ten other offers'" },
      {
        type: "p",
        text: "Just because someone wanted your checklist doesn't mean they want five emails a week about everything else. That's how lists shrink, not grow. Keep it honest, deliver what was promised, and let relevance do the retaining.",
      },
      {
        type: "bottom",
        text: "Bottom line: the easiest way to an optimized lead page is a simple one — one goal, one path, one button worth clicking. Get those right and the conversions rise; get into heat maps and A/B tests after that. If you'd like a specialist's eyes on your pages, [that's our whole business](/website-optimization).",
      },
    ],
  },
  {
    slug: "3-benefits-a-properly-seo-optimized-website-can-give-you",
    links: [
      { label: "SEO & Google Visibility", to: "/seo", note: "Own the searches that matter" },
      { label: "How Much SEO Do I Need for My Small Business?", to: "/much-seo-need-small-business", note: "The honest answer on effort vs. outcome" },
    ],
    category: "SEO",
    graphic: "/img/blog/fresh-content-growth.svg",
    title: "3 Benefits of a Properly SEO Optimized Website",
    description:
      "More visibility, more qualified traffic and a better user experience — the three compounding benefits of a properly SEO-optimized website, in plain English.",
    excerpt:
      "An optimized website doesn't just rank — it converts. Here are the three benefits that compound: visibility, qualified traffic and a visitor experience that sells.",
    readTime: "4 min read",
    date: "2026-10-20",
    content: [
      {
        type: "p",
        text: "A properly optimized website pays you three ways at once — and each benefit makes the next one stronger. Here's the plain-English version of what [SEO optimization](/seo) actually buys you.",
      },
      { type: "h2", text: "1) Increased visibility — which means brand awareness" },
      {
        type: "p",
        text: "When your site shows up for the searches your customers make, they don't just click — they remember. Repeated appearances across their searches build recognition and trust before anyone fills in a form. **That awareness is what turns a stranger into the person who calls you first.**",
      },
      { type: "h2", text: "2) Increased organic traffic — the qualified kind" },
      {
        type: "p",
        text: "Higher rankings bring more visitors, and search traffic arrives pre-qualified: these are people searching for what you already do. Target the [keywords your real customers use](/blog/website-ranking-factors-you-need-to-know-about) and the traffic isn't just bigger — it's the converting kind. **It's also the cheapest traffic you'll ever have**, since it doesn't carry a per-click bill.",
      },
      { type: "h2", text: "3) A better user experience — which makes buying easier" },
      {
        type: "p",
        text: "Good SEO and good experience are the same work: fast-loading pages, simple navigation, [mobile-first layouts](/is-your-website-mobile-ready-why-your-mobile-site-needs-to-be-optimized-for-conversion), clear answers. Visitors stay longer, bounce less, and convert more — and Google reads those same signals as reasons to rank you higher. The wheel compounds.",
      },
      { type: "h2", text: "What it adds up to" },
      {
        type: "ul",
        items: [
          "More revenue from traffic you're not paying per-click for.",
          "A visible, trusted brand in your market.",
          "A durable edge over competitors whose sites sit still.",
          "A traffic source that keeps paying long after the work is done.",
        ],
      },
      {
        type: "bottom",
        text: "Bottom line: visibility brings the click, qualified traffic brings the need, and a good experience closes it. Optimize for all three and your website stops being a brochure and starts being a salesperson. That's the work we do every week.",
      },
    ],
  },
  {
    slug: "10-ways-to-screw-up-your-online-ad-campaign",
    links: [
      { label: "Google Ads Management", to: "/ad-remarketing", note: "Search intent, turned into calls" },
      { label: "Packages", to: "/packages", note: "See the Google Ads offer at a glance" },
    ],
    category: "Paid Ads",
    graphic: "/img/blog/leads-growth.svg",
    title: "10 Ways to Screw Up Your Online Ad Campaign",
    description:
      "The ten most common ways businesses burn money on online ads — from hiring the wrong person to never watching the numbers — and what to do instead.",
    excerpt:
      "Most ad budgets don't fail on the platform — they fail on the decisions around it. Here are the ten classic ways campaigns get torched, so you can avoid every one.",
    readTime: "5 min read",
    date: "2026-10-20",
    content: [
      {
        type: "p",
        text: "Online ads are the fastest way to put your business in front of buyers — and the fastest way to set money on fire when they're run casually. After years of rescuing campaigns, here are the ten ways we see them screwed up most.",
      },
      {
        type: "ul",
        items: [
          "Hiring your friend, brother-in-law or friend's brother-in-law as marketing director. Familiarity isn't a strategy — and neither is a hobbyist with your budget.",
          "Doing nothing because you don't think you can compete. Your competitors thank you personally for this one.",
          "Continuing to do what you've always done while the results keep sliding — and hoping the market comes back around to you.",
          "Trying to handle everything yourself. **Running the trucks and running a bidding strategy are different jobs.**",
          "Never researching the competition — their ads, their offers, [their landing pages](/design-lead-pages-better-conversions). You're bidding blind.",
          "Not monitoring your ads and costs daily. Ad platforms shift constantly; **an unwatched campaign quietly drifts expensive.**",
          "The 'I tried it once, it didn't work, never again' stance — one unmanaged test proves nothing about the channel.",
          "Hiring the biggest agency in town when you're the smallest advertiser on their roster. You'll feel the account manager rotate.",
          "Never changing the ads. Creative fatigue is real — the same ad shown to the same audience decays week after week.",
          "Not thinking ahead and assuming you'll never need to advertise. Markets shift; the businesses with a pipeline ready catch the wave.",
        ],
      },
      {
        type: "p",
        text: "Notice the pattern: almost none of these failures happen inside the ad platform. They happen in the decisions around it — who runs it, how it's watched, and whether it's given a real chance with real attention. That's why our [Google Ads Management](/ad-remarketing) exists: search intent, turned into calls, with the numbers watched daily.",
      },
      {
        type: "bottom",
        text: "Bottom line: ads don't fail because the platform stopped working — they fail because nobody's steering. Avoid the ten classics, watch the numbers daily, and let a specialist handle the parts that aren't your job.",
      },
    ],
  },
  {
    slug: "much-seo-need-small-business",
    links: [
      { label: "SEO & Google Visibility", to: "/seo", note: "The program behind first-page visibility" },
      { label: "Packages", to: "/packages", note: "See the SEO offer at a glance" },
    ],
    category: "SEO",
    graphic: "/img/blog/signs-content-update.svg",
    title: "How Much SEO Do I Need for My Small Business?",
    description:
      "The straight answer on how much SEO a small business really needs: what it delivers, what it costs, how long it takes, and when to bring in a specialist.",
    excerpt:
      "You don't need endless SEO — you need the right amount, aimed at the customers already searching for you. Here's the honest breakdown for small businesses.",
    readTime: "5 min read",
    date: "2026-10-20",
    content: [
      {
        type: "p",
        text: "It's the question behind almost every first phone call: how much SEO do I actually need? Not the agency answer — the real one. Because every business needs some, almost none need everything, and the ones who win are the ones who match the effort to the customers already searching for them.",
      },
      { type: "h2", text: "Why SEO at all?" },
      {
        type: "p",
        text: "Because that's where the buyers are. **The overwhelming majority of searchers never see page two**, and searchers trust what they find at the top — a high ranking is a quiet vote of confidence, the same way the first page of the old phone book was. Show up repeatedly for the searches your customers make and you gain mindshare with every search, even before the click.",
      },
      { type: "h2", text: "What the right amount of SEO does for you" },
      {
        type: "ul",
        items: [
          "Brings qualified traffic — people actively looking for what you sell, not passersby.",
          "Builds trust and credibility before the first conversation happens.",
          "Wins [local searches](/blog/seo-and-local-geo), which for most small businesses is where the money is: maps, near-me queries, and reviews.",
          "Compounds — unlike an ad that stops the moment you stop paying.",
        ],
      },
      { type: "h2", text: "The honest math" },
      {
        type: "p",
        text: "SEO is the rare channel where the traffic you earn doesn't carry a per-click bill. A magazine ad is trash by next month; a page that ranks can deliver leads for years. The trade: it's not instant. **Expect roughly six to twelve months to reach strong positions**, and expect the work to be ongoing — algorithms change, competitors publish, and the sites that keep fresh keep ranking.",
      },
      { type: "h2", text: "So how much do YOU need?" },
      {
        type: "p",
        text: "A practical starting point for a small business: a technically sound, fast website; one strong page per service you sell; your [Google Business Profile](/google-business-profile) complete and active; and a steady rhythm of content aimed at the questions your customers actually type. That's the core program — more than many of your competitors are doing, and far less effort than a full-scale campaign.",
      },
      {
        type: "p",
        text: "What you don't need: campaigns chasing keywords that never produce customers, or a dozen thin pages. If an SEO provider can't tell you which searches they're trying to win and why, that's the tell you're buying activity instead of outcomes.",
      },
      { type: "h2", text: "DIY or hire?" },
      {
        type: "p",
        text: "The basics you can start this week — and we'd encourage it. What most owners hand off is the consistency: the monitoring as algorithms shift, the content cadence, and the technical follow-through. That's exactly what our [SEO & Google Visibility program](/seo) does, and you can see the whole offer at a glance on our [packages page](/packages).",
      },
      {
        type: "bottom",
        text: "Bottom line: you need exactly as much SEO as it takes to be found, chosen and trusted for the searches that matter to your business — no more, no less. Aim it at real customer searches, give it six honest months, and it becomes the cheapest lead source you have.",
      },
    ],
  },
  {
    slug: "5-signs-its-time-to-update-your-websites-content-and-how-to-do-it-right",
    links: [
      { label: "SEO & Google Visibility", to: "/seo", note: "Keep your pages winning the searches that matter" },
      { label: "Website Design & Optimization", to: "/website-optimization", note: "The service behind a site that pulls its weight" },
    ],
    category: "Content Refresh",
    graphic: "/img/blog/signs-content-update.svg",
    title: "5 Signs It's Time to Update Your Website's Content",
    description:
      "Declining traffic, outdated stats, high bounce rates, weak conversions and slipping rankings — the five signs your content needs a refresh, and how to fix it.",
    excerpt:
      "Your website is the heart of your marketing strategy. Here are the five signs its content has gone stale — and the exact steps we use to bring it back.",
    readTime: "8 min read",
    date: "2026-10-20",
    content: [
      {
        type: "p",
        text: "Your website is the heart of your marketing strategy and your most valuable digital asset. If you're letting it sit static, you're missing out on the benefits of SEO — businesses that regularly [update their content](/fresh-content) see dramatically higher lead growth than those that don't. The question most owners have, though, is a practical one: how do I know when it's actually time to update?",
      },
      { type: "h2", text: "The five indicators that your content needs a refresh" },
      {
        type: "p",
        text: "In this guide, we'll look at the most important signs it's time for a refresh — and exactly how you go about fixing each one.",
      },
      { type: "h2", text: "Sign 1 — Declining website traffic" },
      {
        type: "p",
        text: "Identifying the issue: the biggest red flag is a drop in site traffic. Either your content isn't engaging readers anymore, or your SEO isn't strong enough for readers to find it in the first place.",
      },
      {
        type: "p",
        text: "How to address it: start with your back-end analytics — Google's free tools show you which pages are losing traffic and at what rate. From there, revitalize the falling pages with updated keywords, current stats and fresh visuals, or optimize what's there with more engaging titles, meta descriptions and calls to action.",
      },
      { type: "img", src: "/img/blog/signs-refresh-tools.svg", alt: "Illustration of a magnifying glass and wrench reviewing a web page" },
      { type: "h2", text: "Sign 2 — Outdated information or statistics" },
      {
        type: "p",
        text: "Identifying the issue: when you mislead readers with stale data, you tarnish your authority and credibility. If your content cites statistics or time-sensitive facts, they need checking — and in 2026, that includes how your business [shows up in AI answers](/blog/how-to-show-up-in-ai-answers), not just search results.",
      },
      {
        type: "p",
        text: "How to address it: run a content audit. Crawl tools put outdated content in the crosshairs. Cross-check stats, incorporate new insights and trending keywords, and make sure external links still land where they should. Start with your highest-traffic pages and work down.",
      },
      { type: "h2", text: "Sign 3 — High bounce rate and low engagement" },
      {
        type: "p",
        text: "Identifying the issue: a high bounce rate usually means the content isn't capturing attention — most people click away within seconds if the page doesn't obviously answer their search.",
      },
      {
        type: "p",
        text: "How to address it: improve readability and structure first — short paragraphs, bullet points, and visuals make content digestible. Then make it more engaging: video, before-and-afters, and clear CTAs that guide visitors from page to page all keep people on the site longer.",
      },
      { type: "h2", text: "Sign 4 — Low conversion rates" },
      {
        type: "p",
        text: "Identifying the issue: you're getting decent organic traffic but not the conversions you should be. That usually means a disconnect between what visitors are looking for and what the page delivers.",
      },
      {
        type: "p",
        text: "How to address it: get precise about who you're writing for, then update the content with stronger, more specific calls to action. If you're not sure what will work, A/B test one page at a time and let the results decide.",
      },
      { type: "h2", text: "Sign 5 — Poor SEO performance" },
      {
        type: "p",
        text: "Identifying the issue: search engines — and now AI assistants like ChatGPT, Gemini and Google's AI Overviews — constantly change what they reward. If rankings are slipping, stale content is a common culprit.",
      },
      {
        type: "p",
        text: "How to address it: keep updating content with relevant keywords, optimize meta tags so people click through, and strengthen [on-page SEO](/blog/website-ranking-factors-you-need-to-know-about) with proper headers, alt text and internal linking. Routine SEO audits are how you find underperforming pages before they cost you a quarter.",
      },
      { type: "h2", text: "How to effectively update your content" },
      {
        type: "p",
        text: "The right refresh strategy does wonders: HubSpot built a whole strategy around updating existing posts and **grew monthly organic search visits by over 100%**. Here's the step-by-step process we recommend to clients.",
      },
      {
        type: "ul",
        items: [
          "Keyword research and optimization: use keyword tools to find the phrases your audience actually types, and work them naturally into your pages and posts.",
          "Content review and enhancement: remove anything outdated — stats, screenshots, offers — and add depth where readers want more.",
          "Multimedia and visuals: fresh photos and video keep visitors engaged, and engagement feeds rankings.",
          "Internal and external links review: internal links should point to relevant pages; broken external links should be removed at once.",
          "Technical optimization: faster load times, clean meta tags and sensible structure make the site easier to crawl.",
          "Engagement enhancements: refresh your CTAs and make sharing easy.",
          "Tools worth using: keyword research (Ahrefs, Semrush, Google Keyword Planner), site auditing (Screaming Frog, Moz), and optimization checks (Google Search Console, Yoast).",
        ],
      },
      { type: "h2", text: "Leveraging professional services" },
      {
        type: "p",
        text: "You can do a lot of this yourself. But when the team lacks SEO depth, the algorithm keeps moving, or you need a thorough audit to find the gaps, a specialist pays for itself — especially for large-scale content updates. That's [the work we do every week](/seo) for our clients.",
      },
      {
        type: "bottom",
        text: "Bottom line: a static website quietly leaks leads. Watch for the five signs, refresh with purpose — current keywords, current facts, current calls to action — and your best content keeps earning for years. If you want it handled, that's our whole business.",
      },
    ],
  },
  {
    slug: "double-your-leads-in-just-3-months",
    links: [
      { label: "Marketing Strategy & Consulting", to: "/coaching-consulting", note: "A plan built around your business, not a template" },
    ],
    category: "Online Presence",
    graphic: "/img/blog/leads-growth.svg",
    title: "How a Polished Online Presence Doubles Your Leads",
    description:
      "A month-by-month plan to double your leads in 90 days: a website that converts, a Google Business Profile that wins the map pack, social media that compounds.",
    excerpt:
      "First impressions decide who gets the call. Here's the simple three-month plan — website, Google Business Profile, social — that turns an online presence into a lead machine.",
    readTime: "6 min read",
    date: "2026-10-20",
    content: [
      {
        type: "p",
        text: "**First impressions are everything.** When a potential customer searches for your business, that first impression decides whether they engage with you or see what else is out there. Most customers research online before they ever buy — so the question worth asking right now is: is your online presence convincing people to choose you over your competitors?",
      },
      {
        type: "p",
        text: "A real online presence isn't posting on Facebook every other day. It's three things working together — a website, a Google Business Profile, and social media — each with its own job. Your website is the hub where customers end up. Your Google Business Profile is the bridge that gets them there from local searches. Social media keeps the connection warm. Here's the month-by-month plan that turns all three into leads.",
      },
      { type: "h2", text: "Month 1 — Turn your website into a lead-generating machine" },
      {
        type: "p",
        text: "Visitors form an opinion of your business in a fraction of a second. If they don't find what they like within five, most leave. So Month 1 is about the first screen: make it fast, make it obvious, make it easy to act.",
      },
      {
        type: "ul",
        items: [
          "Optimize performance — fast-loading and flawless on a phone.",
          "Add clear, compelling CTAs on every page that matters.",
          "Use [SEO](/seo) so customers actually find the pages you've polished.",
        ],
      },
      { type: "img", src: "/img/blog/leads-social-hub.svg", alt: "Illustration of a website at the center of Google Business Profile, reviews, social media and lead forms" },
      { type: "h2", text: "Month 2 — Optimize your Google Business Profile for local traffic" },
      {
        type: "p",
        text: "For local businesses, the [Business Profile](/google-business-profile) is the single most powerful free tool there is. The more complete, consistent and active it is, the more visible you are in the map pack — the three results that collect most of the local clicks.",
      },
      {
        type: "ul",
        items: [
          "Fill out every section: name, address, phone, hours, description, services.",
          "Use the keywords your customers use in your description.",
          "Ask satisfied customers for reviews — and reply to them all.",
          "Upload real, high-resolution photos of your work, team and location.",
        ],
      },
      { type: "h2", text: "Month 3 — Build engagement through social media" },
      {
        type: "p",
        text: "Social media is how the relationship compounds. Pick the one platform where your customers actually are and do it properly — consistent branding, a mix of photos, video and useful tips, plus targeted ads to reach the people who haven't found you yet. **Consistency beats cleverness**: a steady rhythm of real posts out-performs sporadic brilliance every time.",
      },
      { type: "h2", text: "The results, compounded" },
      {
        type: "p",
        text: "Do those three months in order and the effects stack: the website converts the traffic, the Business Profile feeds it local searchers, and social keeps your name in front of both. That's how online presence turns into a doubling of leads — not one big trick, but **three small engines running together**.",
      },
      { type: "h2", text: "Why you don't need to do it alone" },
      {
        type: "p",
        text: "All of this is doable — and it's also a full-time job to do well. If you'd rather run the business than the marketing calendar, our Marketing Strategy & Consulting service builds the plan with you and keeps it moving. Start with a [free strategy call](/free-strategy-call) and bring your goals.",
      },
      {
        type: "bottom",
        text: "Bottom line: your website, your Google Business Profile and your social media are one storefront with three doors. Polish them in that order, one month at a time, and 90 days from now the phone rings noticeably more. Start today.",
      },
    ],
  },
  {
    slug: "5-reasons-to-update-your-wordpress-website",
    links: [
      { label: "Cross-Web Repair Program", to: "/cross-web-repair", note: "For sites that are already broken or outdated" },
      { label: "Website Design & Optimization", to: "/website-optimization", note: "Keep your site fast, secure and current" },
    ],
    category: "Website Care",
    graphic: "/img/blog/wordpress-updates.svg",
    title: "5 Reasons to Update Your WordPress Website + HTTPS",
    description:
      "Outdated WordPress sites lose speed, security and sales. The five reasons to update — including the HTTPS and mixed-content check most owners skip.",
    excerpt:
      "Your website is your digital storefront — and an out-of-date one costs you speed, security and sales. Here are the five reasons updates can't wait, plus the HTTPS check most owners miss.",
    readTime: "6 min read",
    date: "2026-10-20",
    content: [
      {
        type: "p",
        text: "Your website is your digital storefront, and keeping it updated is essential to maintaining performance, security and functionality. Neglecting updates leads to serious problems: a site that breaks beyond easy repair, plugin conflicts that kill functionality, speed drops that crush both user experience and SEO, lost sales while the site is down — and eventually, a website so outdated that a full rebuild becomes the only option.",
      },
      { type: "h2", text: "1) Improved speed and performance" },
      {
        type: "p",
        text: "Website speed is a key factor in user experience and [search rankings](/blog/website-ranking-factors-you-need-to-know-about). Google prioritizes fast-loading sites, so slow speeds hurt SEO and conversions. Every WordPress update brings performance improvements — faster sites keep visitors engaged, reduce bounce rates, and turn more visits into inquiries and sales.",
      },
      { type: "h2", text: "2) Bug fixes and stability" },
      {
        type: "p",
        text: "No software is perfect, and bugs surface over time. WordPress developers find and fix issues continuously — but those fixes only reach you if you update. Running an outdated core, theme or plugin means struggling with glitches that were solved releases ago.",
      },
      { type: "h2", text: "3) Enhanced security against cyber threats" },
      {
        type: "p",
        text: "WordPress powers a huge share of the internet, which makes it a prime target for attackers. Outdated versions are the easiest way in — regular updates **patch known vulnerabilities and are the single cheapest security measure you can take**. Protecting customer data and your business information starts here.",
      },
      { type: "img", src: "/img/blog/wordpress-https.svg", alt: "Illustration of a browser showing an HTTPS address with a padlock and security shield" },
      { type: "h2", text: "4) And while you're at it: make sure the whole site runs on HTTPS" },
      {
        type: "p",
        text: "Updates keep the software patched; HTTPS keeps the connection itself secure. HTTPS is the encrypted version of the web's core protocol — it authenticates your site and scrambles the data passing between you and your visitors so third parties can't read or tamper with it. **Every modern site should run on it**, and every resource the page loads (images, scripts, fonts) needs to come from secure sources too.",
      },
      {
        type: "ul",
        items: [
          "Trust: browsers label non-HTTPS pages 'Not secure' — right next to your business name.",
          "Rankings: Google has confirmed HTTPS as a search ranking signal, and secure sites keep full referral data in analytics.",
          "Integrity: encryption prevents tampering and injected content between your server and the visitor.",
          "Quick audit: run Google Lighthouse or check for 'mixed content' warnings — any page still loading pieces over http:// should be fixed right away.",
        ],
      },
      { type: "h2", text: "5) A planned update strategy beats clicking 'update'" },
      {
        type: "p",
        text: "A well-planned update routine keeps the site functional while reducing the risk of conflicts: enable automatic updates for minor security releases, get notified of major ones, schedule regular manual updates with a backup first — and if you'd rather never think about it, hand the routine to a [maintenance specialist](/website-optimization).",
      },
      { type: "h2", text: "Take action today" },
      {
        type: "p",
        text: "An outdated website quietly costs you leads, customers and sales. Don't wait until it breaks — regular updates, backups and an HTTPS check keep it secure, fast and generating business. And if your site is already past the patch stage, our [Cross-Web Repair Program](/cross-web-repair) is built exactly for that.",
      },
      {
        type: "bottom",
        text: "Bottom line: updates are cheaper than rebuilds. Keep WordPress, your plugins and themes current, back up before the big ones, and make sure every page loads over HTTPS — your speed, your security and your sales all depend on it.",
      },
    ],
  },
  {
    slug: "fresh-content",
    links: [
      { label: "SEO & Google Visibility", to: "/seo", note: "Rank for the searches your customers type" },
      { label: "5 Signs It's Time to Update Your Website's Content", to: "/5-signs-its-time-to-update-your-websites-content-and-how-to-do-it-right", note: "Know when your content needs this work" },
    ],
    category: "SEO",
    graphic: "/img/blog/fresh-content-growth.svg",
    title: "Why Fresh Content Is Key to SEO Success",
    description:
      "Fresh content is how search engines decide you're relevant — and how AI assistants decide to recommend you. Why updating matters, and the routine to keep it.",
    excerpt:
      "Set-it-and-forget-it content quietly sinks your rankings. Here's why search engines reward freshness — and the simple routine that keeps your site earning.",
    readTime: "7 min read",
    date: "2026-10-20",
    content: [
      {
        type: "p",
        text: "Most business owners understand they need a strong online presence. Many of those same owners treat their content as set-it-and-forget-it. That mindset quietly damages your search rankings — and increasingly, it also decides whether [AI assistants like ChatGPT and Gemini recommend you at all](/blog/what-is-geo-how-does-it-work). Here's why fresh content matters so much, and the routine that keeps it working.",
      },
      { type: "h2", text: "What role does content actually play in SEO?" },
      {
        type: "p",
        text: "Content is how search engines judge whether you're relevant and authoritative. Google rewards sites that publish helpful, up-to-date material, and other sites prefer to link to pages worth reading — which compounds your authority further. From the search engine's perspective, **high-quality content is the clearest evidence that your site deserves the ranking.** There's no shortcut around this: content is the substance SEO techniques are built to surface.",
      },
      { type: "h2", text: "How search engines value fresh content" },
      {
        type: "p",
        text: "To serve the best results, search algorithms weigh the keywords in a query, [page usability](/top-10-ways-to-improve-your-website-user-experience), the expertise of the source and the searcher's location — and content freshness is a meaningful piece of that puzzle. Newer, up-to-date content is usually more relevant, so it tends to rank higher.",
      },
      {
        type: "ul",
        items: [
          "Indicators of freshness: how recent your posts are, how regularly you update existing pages, and how engaged your audience is.",
          "Crawl frequency: the more you update, the more often search bots visit — and the faster new content gets discovered and ranked.",
        ],
      },
      { type: "h2", text: "The benefits of updating content regularly" },
      {
        type: "p",
        text: "Improved SEO performance: frequent updates mean more crawling and quicker indexing. Keywords also rise and fall in popularity — updating often keeps you on top of the phrases bringing traffic this season, not last year.",
      },
      {
        type: "p",
        text: "Enhanced user experience: as you tailor content to the queries your audience actually uses, your site builds authority in its niche — and authoritative sites earn backlinks, which helps SEO even further.",
      },
      {
        type: "p",
        text: "Increased traffic and engagement: fresh content attracts new visitors and gives current ones a reason to return and share. Time on site, pages per session and bounce rate all move in the right direction — and search engines notice.",
      },
      { type: "img", src: "/img/blog/fresh-cornerstone.svg", alt: "Illustration of supporting articles linking to a central cornerstone page" },
      { type: "h2", text: "Build cornerstone content — and link to it constantly" },
      {
        type: "p",
        text: "The smartest structure most small businesses never build: pick a few pages to be your cornerstones — the deepest, most useful pages you have, the ones that drive conversions. Then link to them from your blog posts and service pages again and again. **Every internal link passes a little authority to those pages**, and over time that's what makes them rank for your most valuable searches.",
      },
      {
        type: "p",
        text: "Pair that with basic on-page habits: target one primary phrase per page, put it in the title and headline, use it naturally through the text, add a few long-tail variations, and link back to the cornerstone page it supports.",
      },
      { type: "h2", text: "Practical tips for keeping content fresh" },
      {
        type: "ul",
        items: [
          "[Content audits](/5-signs-its-time-to-update-your-websites-content-and-how-to-do-it-right): periodically review for outdated stats, bad links, redundant keywords and weak visuals — then fix the worst first.",
          "Update strategies: refresh titles, headers and meta descriptions; add new images or video; bring articles current with this year's facts.",
          "A content calendar: most businesses should aim to publish at least weekly — even four solid posts a month plus one audit makes a real difference.",
          "Avoid the classic traps: keyword stuffing, over-optimization and neglecting older posts do more harm than good. Quality, relevance and readability win.",
        ],
      },
      {
        type: "bottom",
        text: "Bottom line: fresh content is the engine of SEO — it keeps crawlers visiting, readers engaging and AI assistants quoting you. Build cornerstone pages, link to them well, and update on a schedule. If you want the routine handled, that's our whole business.",
      },
    ],
  },
  {
    slug: "attract-more-local-customers",
    links: [
      { label: "Google Business Profile & Local Search", to: "/google-business-profile", note: "Win the local pack in your market" },
      { label: "SEO and Local GEO: Get Found by Customers Near You", to: "/blog/seo-and-local-geo", note: "The guide behind showing up near-me" },
    ],
    category: "Local Search",
    graphic: "/img/blog/gbp-storefront.svg",
    title: "Attract More Local Customers: Optimize Your GBP",
    description:
      "A plain-English, 6-step guide to optimizing your Google Business Profile — so nearby homeowners find you first, trust you fastest and call you instead.",
    excerpt:
      "Your Google Business Profile is your storefront in the search results. Here's the six-step tune-up that turns it into a steady source of local calls.",
    readTime: "6 min read",
    date: "2026-10-20",
    content: [
      {
        type: "p",
        text: "When a homeowner searches \"plumber near me\" or \"AC repair\" after their system dies, the results page shows three businesses in the [map pack](/blog/seo-and-local-geo) before almost anything else. Those three spots get the calls — and they're earned by one thing most owners set up once and forget: the Google Business Profile. Think of it as your storefront on the busiest street in town. **It's open at 2am, it answers questions before you can, and it decides who calls you** versus who calls the next name down the list.",
      },
      { type: "h2", text: "Step 1 — Claim your profile and verify it's yours" },
      {
        type: "p",
        text: "If you've never claimed your profile, someone may have — or worse, an auto-generated listing with the wrong hours and a wrong address is sitting there answering for you. Visit business.google.com, find your business, and claim it. Then verify — Google usually sends a postcard or a phone/email code. Verification is what unlocks your ability to fix everything else on the list, so it's always step one.",
      },
      { type: "img", src: "/img/blog/gbp-storefront.svg", alt: "Illustration of a Google Business Profile listing next to a map with a location pin" },
      { type: "h2", text: "Step 2 — Fill in every field like it's a first phone call" },
      {
        type: "ul",
        items: [
          "Exact business name, address and one phone number that's actually answered — consistency here is how Google matches you to local searches.",
          "Hours that reflect reality, including holiday changes. Showing closed when you're open costs you the emergency call.",
          "Your service area — the towns and zip codes you actually cover, not the whole state.",
          "A description written for the homeowner: what you fix, who you serve, what makes you the safe choice.",
          "Your website, ideally pointing to the [service page](/blog/home-services-content-kansas-city) that matches what most customers call about.",
        ],
      },
      {
        type: "p",
        text: "Every empty field is a question Google can't answer — and every question it can't answer, it gives to a competitor whose profile does.",
      },
      { type: "h2", text: "Step 3 — Choose your categories and keywords like a homeowner would" },
      {
        type: "p",
        text: "Categories tell Google which searches you belong in. Pick the one that names your core trade first — HVAC contractor, plumber, electrician — then add what you genuinely do. Use the words your customers use, too: \"furnace repair\", \"water heater install\", \"panel upgrade\" in your business description and in your service list. You're not stuffing keywords; you're labeling the shelf so the right shopper picks it up.",
      },
      { type: "h2", text: "Step 4 — Turn happy customers into visible proof" },
      {
        type: "p",
        text: "Reviews are the reviews section of your storefront window. Ask every satisfied customer — right after the job, when the goodwill is warm — with a short text link that takes them straight to the review form. Then reply to every review, good or bad, in your own voice. Homeowners read your replies as a preview of what you're like to have in their house. **Volume and recency both matter**: a steady trickle of fresh reviews beats a wall of praise from 2019.",
      },
      { type: "img", src: "/img/blog/gbp-reviews.svg", alt: "Illustration of a phone showing five-star customer reviews" },
      { type: "h2", text: "Step 5 — Post updates so the storefront never looks closed" },
      {
        type: "p",
        text: "Google posts are the window display: seasonal tune-ups before summer, furnace checks before the first freeze, financing offers, finished-job photos. A profile that was last touched a year ago reads like a shop with last season's posters in the window. Fifteen minutes a week of posting keeps it looking alive — to homeowners and to Google, which both reward freshness.",
      },
      { type: "h2", text: "Step 6 — Read your insights and let them steer you" },
      {
        type: "p",
        text: "Your profile's insights show how people found you, what they searched, whether they called, clicked your website, or asked for directions. Watch the trend, not the day: if calls dip while views hold, your hours or photos may be the problem; if views dip, your categories or reviews need attention. It's a dashboard, not a diary — check it monthly and act on what it says.",
      },
      { type: "img", src: "/img/blog/gbp-insights.svg", alt: "Illustration of a performance dashboard with rising call charts and star ratings" },
      { type: "h2", text: "What an optimized profile pays back" },
      {
        type: "ul",
        items: [
          "Visibility in the map pack — the three results that collect most of the local clicks.",
          "Trust before the call: stars, photos and fresh posts do the first impression for you.",
          "Calls and direction requests straight from search — no website visit required.",
          "A website that works harder, because profile visitors arrive already warmed up.",
          "Compounding local recognition — the profile gets stronger every month you keep it current.",
        ],
      },
      { type: "h2", text: "When to get help with it" },
      {
        type: "p",
        text: "Steps 1 through 4 you can do this week. What most owners hand off is the consistency — the monthly posting, the review follow-ups, the insight reviews, and the website work that turns profile visits into booked jobs. That's exactly what our [Google Business Profile & Local Search service](/google-business-profile) handles, one-on-one with Todd.",
      },
      {
        type: "bottom",
        text: "Bottom line: your Google Business Profile is the storefront every local search walks past. Claim it, complete it, collect reviews, post regularly, and read the insights — do that and the map pack starts working like your best sales rep. If you want it handled, that's our whole business.",
      },
    ],
  },
  {
    slug: "update-website-content-home-services",
    links: [
      { label: "Website Design & Optimization", to: "/website-optimization", note: "The service behind a site that pulls its weight" },
      { label: "SEO & Google Visibility", to: "/seo", note: "Own the searches homeowners type at 9pm" },
    ],
    category: "Content Refresh",
    graphic: "/img/blog/update-website-content-home-services.svg",
    title: "Update Your Website Content: A Home Services Guide",
    description:
      "Five signs your home service website content needs a refresh — fewer calls, stale pages, quiet phones — and a plain-English way to fix each one.",
    excerpt:
      "Your website is the hardest-working crew member you have. Here are the five signs its content has gone stale — and the plain-English fix for each one.",
    readTime: "7 min read",
    date: "2026-10-20",
    content: [
      {
        type: "p",
        text: "A homeowner whose water heater just let go doesn't browse — they search, they judge in seconds, and they call whoever answers the question fastest. Your website content is doing that talking for you around the clock. When it goes stale, the calls go quiet — and most owners blame the season, the ads, or the economy before they blame the page. Here are the five signs it's time for a content refresh, and how to fix each one without a marketing degree.",
      },
      { type: "h2", text: "Sign 1 — The phone rings less than it used to" },
      {
        type: "p",
        text: "For home service companies, traffic shows up as rings and form fills before it shows up anywhere else. If calls are sliding, the usual culprit is content that used to rank and doesn't anymore — a service page that hasn't been touched in three years while two newer competitors wrote fresher, more specific pages about the same repair.",
      },
      {
        type: "p",
        text: "How to address it: open Google Search Console (free) and look at which pages lost clicks over the last six months. Nine times out of ten **it's your money pages** — water heater repair, AC replacement, panel upgrades. Refresh those first: sharper titles, this season's specifics, and photos from jobs you actually did this year. You don't need new pages to win back traffic; you need the right ones to stop gathering dust.",
      },
      { type: "h2", text: "Sign 2 — The site tells last year's story" },
      {
        type: "ul",
        items: [
          "A service area list that's missing the three towns you expanded into — homeowners in those towns assume you don't come.",
          "Team photos from a decade ago and crew members who left years ago. Homeowners notice.",
          "Prices, warranties or financing that changed — or equipment brands you no longer carry.",
          "A blog whose newest post is about a season that ended two winters ago.",
        ],
      },
      {
        type: "p",
        text: "How to address it: run a simple content audit. List your pages, and for each one ask one question — is this still true? Fix the money pages first: homepage, each service page, your about page. Outdated trust signals quietly cost you the calls that were one click away.",
      },
      { type: "h2", text: "Sign 3 — Visitors leave without scrolling" },
      {
        type: "p",
        text: "A high bounce rate on a home services site usually means the homeowner couldn't answer their three questions in the first few seconds: Do you do this job? Do you come to my area? How fast can you get here? If that answer is buried under a paragraph about how long you've been family owned, they're gone — probably to the competitor whose page leads with the repair they need.",
      },
      {
        type: "p",
        text: "How to address it: lead every [service page](/blog/home-services-content-kansas-city) with the problem in the homeowner's words — \"Furnace blowing cold air?\" — then answer fast. Short paragraphs, bullets where they help, real photos of your crew and trucks instead of stock smiles, and a clickable phone number near the top. Readable pages don't just convert better; Google reads the same way homeowners do.",
      },
      { type: "h2", text: "Sign 4 — You get visits but not calls" },
      {
        type: "p",
        text: "Traffic with no conversions means the content and the customer aren't meeting. The page talks about the company; the homeowner is there to get their life back to normal before the weekend. There's a disconnect between what the page says and what the visitor needs to hear.",
      },
      {
        type: "p",
        text: "How to address it: rewrite the page around the job to be done. What does the visit involve? What does it usually cost? How soon can you come? Then give every page one clear next step — call, book online, or request a quote — and make it work on a phone, because that's where emergency searches happen. If you're not sure which version works better, test one page at a time and let the calls decide.",
      },
      { type: "h2", text: "Sign 5 — You've slipped off Google (and now the AI answers)" },
      {
        type: "p",
        text: "Search engines and AI assistants reward pages that stay current. When your rankings slip for \"water heater repair [your town]\" and competitors start appearing above you — and when [ChatGPT or Gemini names them instead of you](/blog/what-is-geo-how-does-it-work) — stale content is a common reason. The engines can't tell homeowners you're the best option if your page hasn't said anything new in two years.",
      },
      {
        type: "p",
        text: "How to address it: refresh pages with the words homeowners actually type, tighten meta titles and descriptions so the click happens, sort your headers, add alt text to photos, and link related pages together — your AC repair page to your maintenance page, your maintenance page to your service area. Do that on a routine, not a rescue mission. An hour a month beats a rebuild every three years.",
      },
      { type: "h2", text: "How to update your content, step by step" },
      {
        type: "ul",
        items: [
          "Keyword research, homeowner edition: write down the phrases you hear on the phone every week — \"AC not cooling\", \"water heater leaking\" — and check what people search per service, per town. Free tools like Google Keyword Planner are enough to start.",
          "Review and enhance: strip anything outdated, then add the depth homeowners want — cost ranges, timelines, what the visit involves. Depth is what separates a page that ranks from a page that fills space.",
          "Update photos and visuals: real crew, real trucks, real before-and-afters. Fresh, honest visuals hold visitors longer — and time on page is a signal Google trusts.",
          "**Fix your links: every internal link should send the visitor somewhere useful, and every broken external link should go.** Broken links on a trades site read like a truck with a dented door — technically fine, but it makes you wonder.",
          "Tighten the technical basics: fast load times, clean meta tags, sensible headers, alt text. Speed matters double in home services — half your traffic is on a phone in a kitchen at 9pm.",
          "Refresh your CTAs: every page should end with one clear next step. If the button says nothing more than \"submit\", it's leaking calls.",
        ],
      },
      { type: "h2", text: "Free tools that cover most of it" },
      {
        type: "ul",
        items: [
          "Google Search Console — which pages win, which are slipping, and what people searched to find you.",
          "Google Analytics — where visitors go, where they leave, and which pages actually produce calls.",
          "Google PageSpeed Insights — how fast your pages load on the phones homeowners actually use.",
        ],
      },
      { type: "h2", text: "When it's worth bringing in a specialist" },
      {
        type: "p",
        text: "You can do a surprising amount of this yourself. But if the crew is booked, the audits keep getting postponed, or the rankings won't move no matter what you touch, a specialist pays for itself — a proper audit finds the pages leaking the most calls, and a steady hand keeps the refreshes coming while you run the business. That's exactly [the work we do for home service companies](/website-optimization) every week.",
      },
      {
        type: "bottom",
        text: "Bottom line: your website is the one crew member that talks to every homeowner at 2am. Keep its content current — fresh pages, real photos, clear answers, one next step — and it pays for itself many times over. If you want that handled and done right, that's our whole business.",
      },
    ],
  },
  {
    slug: "home-services-content-kansas-city",
    links: [
      { label: "Website Design & Optimization", to: "/website-optimization", note: "The service behind a site that pulls its weight" },
      { label: "Google Business Profile & Local Search", to: "/google-business-profile", note: "Win the local pack in every suburb you serve" },
    ],
    category: "Local Content",
    graphic: "/img/blog/home-services-content-kansas-city.svg",
    title: "Creating Website Content for KC Home Services",
    description:
      "What to put on a home services website that Kansas City customers actually find — service pages, suburbs, seasonal topics and real photos, in plain English.",
    excerpt:
      "A website that wins Kansas City homeowners isn't one long sales page. Here's the content that gets found — service pages, suburbs, seasonal help and real photos.",
    readTime: "6 min read",
    date: "2026-10-13",
    content: [
      {
        type: "p",
        text: "Most home service websites say the same three things: family owned, fair prices, quality work. True or not, it doesn't help a homeowner in Overland Park find you when their AC dies in July. Content is what connects the two — and for Kansas City home service companies, **it's the most underused growth lever there is.**",
      },
      { type: "h2", text: "One page per service, written for the customer" },
      {
        type: "p",
        text: "If you do HVAC, electrical and plumbing, each one [deserves its own page](/blog/update-website-content-home-services) — not one \"Services\" page trying to cover everything. On each page, answer the questions the homeowner is actually Googling at 9pm: What does this cost? How fast can you come? Do you work on my brand? A page that answers those reads like help, not a pitch — and Google notices the same thing.",
      },
      { type: "h2", text: "Say where you work — specifically" },
      {
        type: "p",
        text: "\"Serving the KC metro\" is invisible to Google. Pages and mentions for the places you actually work — Kansas City, Overland Park, Olathe, Lee's Summit, Independence, Shawnee and the rest — tell search engines exactly [which searches you belong in](/blog/seo-and-local-geo). Real jobs you've done in those towns, told briefly, beat any slogan.",
      },
      { type: "h2", text: "Content that earns trust before the call" },
      {
        type: "ul",
        items: [
          "Seasonal help — pre-summer AC checks, furnace questions before the first freeze. KC weather makes this calendar for you.",
          "Real photos of your crew and real jobs — not stock images. Homeowners can tell the difference instantly.",
          "Straight answers to the questions you hear on the phone every week; each one is a page (or FAQ) waiting to be written.",
          "[Reviews and finished-job stories](/attract-more-local-customers), in the customer's own words, near the button to call.",
        ],
      },
      { type: "h2", text: "Keep it honest and keep it coming" },
      {
        type: "p",
        text: "You don't need to publish weekly to win — you need pages that are genuinely useful, accurate, and refreshed when things change. **A dozen strong pages beating twenty thin ones** is the norm, not the exception.",
      },
      {
        type: "bottom",
        text: "Bottom line: Kansas City homeowners search with a problem and a neighborhood. Give every service its own page, name your suburbs, show real work, and answer the questions you already hear on the phone. Do that consistently and the website starts pulling its weight — if you want that built and handled, that's our whole business.",
      },
    ],
  },
  {
    slug: "facebook-ads-using-ai",
    links: [
      { label: "Facebook & Instagram Ads", to: "/facebook-ads", note: "Where Meta's AI works hardest for owners" },
      { label: "Google Remarketing with Google Ads & AdRoll", to: "/blog/google-remarketing-adwords-and-adroll", note: "Stay in front of the visitors who left" },
    ],
    category: "Facebook Ads & AI",
    graphic: "/img/blog/facebook-ads-using-ai.svg",
    title: "Facebook Ads Using AI: A Guide for Owners",
    description:
      "Meta's AI now picks audiences, writes creative and sets budgets on Facebook and Instagram. What business owners should know before turning it loose.",
    excerpt:
      "Meta's AI picks audiences, writes creative and sets budgets on Facebook and Instagram. Here's what to hand over, what to hold on to, and how not to waste the test.",
    readTime: "5 min read",
    date: "2026-10-13",
    content: [
      {
        type: "p",
        text: "[Facebook and Instagram ads](/facebook-ads) run on some of the most powerful advertising AI ever built — and Meta keeps handing it more of the steering wheel. For a business owner, the question isn't \"should I use AI?\" (Meta decides that for you). The question is what to hand over and what to keep.",
      },
      { type: "h2", text: "What Meta's AI does now" },
      {
        type: "ul",
        items: [
          "Audiences — instead of hand-picking interests, Meta's automated campaigns find lookalikes of your customers and hunt for buyers on its own.",
          "Creative — the platform mixes your headlines, images and videos into dozens of combinations and shows whichever performs.",
          "Budget and placement — the AI decides whether your dollar goes to Facebook Feed, Instagram, Reels or the rest, moment by moment.",
        ],
      },
      { type: "h2", text: "What to hand over" },
      {
        type: "p",
        text: "Let the AI do the micro-decisions — which person sees which variation, at which moment. It processes signals no human can, and fighting it usually costs you more.",
      },
      { type: "h2", text: "What to hold on to" },
      {
        type: "ul",
        items: [
          "The offer. AI can't invent a reason to call you — a real promotion, a real photo of your crew, a message that sounds like your business.",
          "The guardrails — spending caps, the locations you serve, and the reminder that **a campaign optimizing for \"engagement\" is not the same as one optimizing for customers**.",
          "The weekly read — which ads earned real inquiries, which burned budget, and what to turn off.",
        ],
      },
      {
        type: "p",
        text: "One honest warning: [AI creative tools](/blog/website-optimization-should-you-use-ai) can generate endless images and copy, but the internet is filling up with look-alike ads. On Facebook and Instagram, **the ads that stop the scroll are the real ones** — actual jobs, actual faces, actual offers.",
      },
      {
        type: "bottom",
        text: "Bottom line: Meta's AI is an excellent driver with no destination. You supply the destination — the offer, the audience that matters, the definition of a good lead — and check the driving weekly. We run exactly that loop for clients every day; [the first conversation is free](/free-strategy-call).",
      },
    ],
  },
  {
    slug: "google-remarketing-adwords-and-adroll",
    links: [
      { label: "Google Ads Management", to: "/ad-remarketing", note: "Search intent, turned into calls" },
      { label: "Facebook & Instagram Ads", to: "/facebook-ads", note: "Reach the audiences ready to buy" },
    ],
    category: "Remarketing",
    graphic: "/img/blog/google-remarketing-adwords-adroll.svg",
    title: "Google Remarketing: AdWords and AdRoll",
    description:
      "Remarketing shows your ads to people who already visited your site. How Google Ads and AdRoll do it, what it costs to try, and when it's worth it.",
    excerpt:
      "Most visitors leave your site without calling. Remarketing is the polite way to follow them around the internet until they're ready — here's how Google Ads and AdRoll do it.",
    readTime: "6 min read",
    date: "2026-10-13",
    content: [
      {
        type: "p",
        text: "Here's the part nobody says out loud about website traffic: almost everyone leaves without doing anything. They had a look, they got distracted, they left. [Remarketing](/ad-remarketing) is the fix — it shows your ads afterward to the people who already met you once. **Warmer audience, higher odds, and usually cheaper clicks** than advertising to total strangers.",
      },
      { type: "h2", text: "How it works, mechanically" },
      {
        type: "p",
        text: "A small piece of code on your website (a tag) quietly notes when someone visits. That drops them into an audience — say \"visited the pricing page but didn't call.\" Then your ads follow them: when they read the news, watch YouTube, scroll their phone. Not spooky in practice — you're just back in the room at the moment they're ready.",
      },
      { type: "h2", text: "The two tools in the title" },
      {
        type: "ul",
        items: [
          "Google Ads (AdWords) remarketing — show follow-up ads across Google's display network, in YouTube, and in search itself for people who already know you. Best when your customers are on Google's enormous map of sites and apps.",
          "AdRoll — a separate platform that runs your remarketing across many networks from one dashboard and layers in email follow-ups. Handy when you want one place to manage retargeting beyond Google alone.",
        ],
      },
      { type: "h2", text: "What matters more than the tool" },
      {
        type: "ul",
        items: [
          "Clean audiences — recent visitors, people who saw your pricing, people who started to contact you and stopped. \"Everyone from the last 90 days\" is lazy and it shows.",
          "Fresh creative — the same banner for months becomes furniture. Rotate the message; give a reason to come back.",
          "A sane frequency cap — following up is service; ten ads a day is a grudge.",
          "Real tracking — did the people who saw the follow-up ads actually call or book? **That's the only scorecard that matters.**",
        ],
      },
      {
        type: "bottom",
        text: "Bottom line: remarketing is the highest-leverage use of ad money most small businesses never set up — you're advertising to people who already raised their hand. Whether it runs through Google Ads, AdRoll or both should be a strategy decision, not a default. We set these up and babysit them weekly; [bring your website and we'll tell you honestly if it's worth it](/free-strategy-call).",
      },
    ],
  },
  {
    slug: "google-ads-and-ai",
    links: [
      { label: "Google Ads Management", to: "/ad-remarketing", note: "How we run AI-assisted campaigns day to day" },
      { label: "SEO and local GEO: how AI decides who gets found", to: "/blog/seo-and-local-geo", note: "The other half of being findable" },
    ],
    category: "Google Ads & AI",
    graphic: "/img/blog/google-ads-and-ai.svg",
    title: "Google Ads and AI: What Business Owners Should Know",
    description:
      "Google Ads is run by AI now — smart bidding, auto-made ads and AI answers above results. A plain-English guide for business owners, from Todd Meisler.",
    excerpt:
      "Google Ads is run by AI now — smart bidding, auto-built ads and AI answers above the results. Here's what that actually means for your budget, in plain English.",
    readTime: "6 min read",
    date: "2026-10-13",
    content: [
      {
        type: "p",
        text: "If you ran Google Ads a few years ago and you're looking at the platform today, it would feel like a different product. Almost everything is now decided by artificial intelligence — how much you pay per click, who sees your ads, even what the ads say. That's not a reason to avoid it. It's a reason to understand it before you spend.",
      },
      { type: "h2", text: "First, what Google Ads actually is" },
      {
        type: "p",
        text: "Every time someone searches on Google, an auction happens in a fraction of a second. Advertisers bid for the chance to appear, and you only pay when someone clicks. That part hasn't changed. If someone searches \"water heater repair near me,\" the businesses at the top of the page are there because of this auction — and a phone call from that search is worth far more than a flyer that hits the wrong mailbox.",
      },
      { type: "h2", text: "Where AI took over" },
      {
        type: "p",
        text: "Three things changed, and each one matters to your wallet:",
      },
      {
        type: "ul",
        items: [
          "Smart bidding. Google now sets your bid for every single search, guessing how likely that person is to buy. When it has good data, it's better at this than any human. When it doesn't, it guesses anyway.",
          "AI-built campaigns. Newer campaign types let Google write your headlines, pick your images and decide where your money goes across Search, Maps, YouTube and partner sites. Less work for you — but also less control.",
          "AI answers above the results. Google increasingly shows an [AI-written answer at the top of the page](/blog/how-to-show-up-in-ai-answers) before people see the ads or the regular listings. That changes how many people even reach an ad, and it's still shifting.",
        ],
      },
      { type: "h2", text: "What this means for your budget" },
      {
        type: "p",
        text: "The AI is only as good as what it's told to optimize for. If the only signal it gets is \"clicks,\" it will happily buy you a lot of cheap, useless clicks. If it can see which clicks turned into phone calls and form submissions, it starts hunting for customers instead of visitors. That's why **conversion tracking comes before clever ad copy** — it's the steering wheel for the whole machine.",
      },
      {
        type: "p",
        text: "The other change is that the guardrails matter more, not less. AI will test wild combinations of headlines and search terms. Some will be great. Some will be the wrong kind of attention. **Someone still has to look at the search terms report, exclude the junk**, and keep the campaign pointed at services that actually make you money.",
      },
      { type: "h2", text: "Where a human still earns their keep" },
      {
        type: "ul",
        items: [
          "Setting up call and form tracking so the AI optimizes toward revenue, not clicks",
          "Choosing what NOT to spend on — the services with thin margins, the towns you don't serve",
          "Reading the results weekly and making the calls a dashboard can't make",
          "[Remarketing](/blog/google-remarketing-adwords-and-adroll) — staying in front of the people who visited and didn't call yet",
        ],
      },
      {
        type: "bottom",
        text: "Bottom line: Google Ads with AI is the fastest way to get in front of people who are searching for what you sell — but it rewards businesses that feed it honest data and prune it weekly. That's the work. If you'd rather hand that off, [that's literally what we do every day](/ad-remarketing).",
      },
    ],
  },
  {
    slug: "seo-and-local-geo",
    links: [
      { label: "SEO & Google Visibility", to: "/seo", note: "Own the searches that matter" },
      { label: "Google Business Profile & Local Search", to: "/google-business-profile", note: "The local pack, explained and won" },
    ],
    category: "SEO & Local GEO",
    graphic: "/img/blog/seo-local-geo.svg",
    title: "SEO and Local GEO: Get Found by Customers Near You",
    description:
      "What SEO and local search really are — the map pack, Google Business Profile, reviews and near-me searches — explained in plain English for business owners.",
    excerpt:
      "SEO isn't magic and it isn't a mystery box. Here's what actually decides whether a customer near you finds you or finds your competitor — in plain English.",
    readTime: "6 min read",
    date: "2026-10-13",
    content: [
      {
        type: "p",
        text: "SEO — search engine optimization — is just making your website the best answer to the questions your customers type into Google. Do it well and your site shows up without you paying for every click. For a local business, there's a second layer that matters even more: [local SEO](/blog/kansas-city-home-services-local-search), the geography game. That's the part that decides who gets the call when someone searches \"plumber near me.\"",
      },
      { type: "h2", text: "The map pack is the new front page" },
      {
        type: "p",
        text: "Search \"electrician\" on your phone and you'll see a map with three businesses under it. That's the map pack, and for local services **it's the most valuable real estate on the internet**. Google picks those three using three rough ingredients: how relevant your business is to the search, how close you are to the searcher, and how prominent you are — your reviews, your reputation, how well-known your business is around the web.",
      },
      { type: "h2", text: "Where the points come from" },
      {
        type: "ul",
        items: [
          "Your [Google Business Profile](/how-to-create-an-effective-google-business-profile-for-local-seo) — filled out completely, with the right service categories, real photos and fresh posts. This is your listing for the map pack, and an empty profile is a locked front door.",
          "[Reviews](/attract-more-local-customers) — steady, recent and answered. A business with 40 recent reviews beats a business with 4 old ones, and replying shows Google (and customers) that you're paying attention.",
          "Your website pages — a real page for each service and each area you serve, written for humans. A page that says exactly what you do and where you do it will outrun a homepage trying to say everything.",
          "Consistency — your business name, address and phone number matching everywhere it appears online. Google cross-checks, and **mismatches cost you trust**.",
          "The technical basics — a site that loads fast, works on phones, and doesn't confuse Google about what each page is about.",
        ],
      },
      { type: "h2", text: "How long it takes" },
      {
        type: "p",
        text: "Honest answer: it compounds. Some fixes — completing a profile, fixing a broken page — show up in weeks. Rankings for competitive searches take months of steady work, because you're outranking other businesses who are also working on it. Anyone promising overnight number-one is selling you something else.",
      },
      {
        type: "bottom",
        text: "Bottom line: local customers search with their thumbs before they ever call. SEO plus local GEO work is how your business is the name they find first — and unlike ads, the ranking keeps working after you stop paying for the click. We build that system for a living; the first read of where you stand is free.",
      },
    ],
  },
  {
    slug: "website-optimization-should-you-use-ai",
    links: [
      { label: "Website Design & Optimization", to: "/website-optimization", note: "What a faster, sharper site does for leads" },
      { label: "SEO and local GEO: how AI decides who gets found", to: "/blog/seo-and-local-geo", note: "Where AI search is heading next" },
    ],
    category: "Website Optimization & AI",
    graphic: "/img/blog/website-optimization-ai.svg",
    title: "Website Optimization: Should You Use AI?",
    description:
      "Can AI tools like ChatGPT optimize your website? Where AI genuinely helps business owners, where it quietly fails, and how to use it without hurting your site.",
    excerpt:
      "AI can write your headlines, suggest keywords and fix code. It can also quietly make your website worse. An honest look at where AI helps website optimization — and where it doesn't.",
    readTime: "5 min read",
    date: "2026-10-13",
    content: [
      {
        type: "p",
        text: "Business owners ask me this weekly: \"Can I just use AI to [optimize my website](/website-optimization)?\" The honest answer is yes, partly — and the parts where AI shines are not the parts most people assume.",
      },
      { type: "h2", text: "First, what website optimization even is" },
      {
        type: "p",
        text: "It's making your site do its job better: loading fast, making sense in the first five seconds, answering the questions a real customer has, and making the next step — call, book, buy — obvious. Speed, structure, words and measurement. That's the whole game, and every part of it is judged by two audiences at once: human beings and Google.",
      },
      { type: "h2", text: "Where AI genuinely helps" },
      {
        type: "ul",
        items: [
          "First drafts. AI writes a workable draft of a service page or a meta description in seconds. Editing a draft is faster than staring at a blank page.",
          "Explaining things. \"What's a good page speed score?\" or \"why is my bounce rate high?\" — AI is a patient teacher for the vocabulary.",
          "Small technical fixes. Snippets for [redirects](/blog/a-301-vs-a-302-redirect), schema markup, image sizing — common problems with common answers.",
          "Brainstorming. Ten headline options, five FAQ ideas, alternate ways to phrase a service. Volume is where it's unbeatable.",
        ],
      },
      { type: "h2", text: "Where AI quietly fails you" },
      {
        type: "ul",
        items: [
          "It doesn't know your customers. It has never sat with one of your best clients or heard why they really called. The words that convert are in those conversations, not in a statistical average of the internet.",
          "It invents facts. AI will confidently write services you don't offer, guarantees you never made, and hours you don't keep. On a business website, that's not a typo — it's a liability.",
          "It sounds like everyone else. **Content generated at arm's length reads generic**, and Google has spent years getting better at rewarding pages with real experience behind them.",
          "It can't see your numbers. AI doesn't know which page actually generates your calls or where visitors give up. **Optimization without measurement is just redecoration.**",
        ],
      },
      { type: "h2", text: "So, should you use it?" },
      {
        type: "p",
        text: "Use AI as the assistant, not the strategist. Let it draft; you decide. Let it explain; your numbers decide. And before anything goes live, someone who knows your business — you or someone you trust with it — should read every word and check every claim.",
      },
      {
        type: "bottom",
        text: "Bottom line: AI makes website optimization faster, not automatic. The wins still come from knowing your customer and watching your real numbers. If you want a straight answer on where your site stands — and what's worth doing first — that's exactly what a [free strategy call](/free-strategy-call) is for.",
      },
    ],
  },
];

// Keep-reading picks: score every other article by shared SEO keywords
// (title, description, category) — same keyword themes the inline links target.
const STOPWORDS = new Set(
  "a,an,the,and,or,of,to,in,for,your,you,we,it,is,are,on,with,that,this,what,how,why,when,do,does,should,i,my,our,their,they,its,as,be,by,at,from,not,can,will,more,most,about,into,than,then,them,there,here,been,being,have,has,had,were,was,before,after,while,those,these,using,used,use,get,got,one,two".split(",")
);
const keywordTokens = (s) =>
  String(s)
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .filter((w) => w.length > 3 && !STOPWORDS.has(w));

export const relatedArticles = (article, n = 3) => {
  const mine = new Set([
    ...keywordTokens(article.title),
    ...keywordTokens(article.description),
    ...keywordTokens(article.category),
  ]);
  return ARTICLES.filter((a) => a.slug !== article.slug)
    .map((a) => {
      const overlap = [
        ...keywordTokens(a.title),
        ...keywordTokens(a.description),
        ...keywordTokens(a.category),
      ].filter((t) => mine.has(t)).length;
      return { a, score: overlap * 2 + (a.category === article.category ? 1 : 0) };
    })
    .sort(
      (x, y) =>
        y.score - x.score || Date.parse(y.a.date) - Date.parse(x.a.date)
    )
    .slice(0, n)
    .map(({ a }) => a);
};
